import React, { useEffect, useRef, useCallback, useMemo, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useTranslation } from '@papan/i18n'
import { Code, Loader2, AlertCircle, Upload, Clock, PanelLeftOpen, PanelRightOpen, Play, Settings, AppWindow as AppWindowIcon } from 'lucide-react'
import { useConflict, useConfirm, useToast, ErrorBoundary, CommandRegistryProvider, useCommandScope, useFocusScope, useUndoRegion, BreadcrumbPath, ShellTopBarPortal, useWhenShown, DraftScopeProvider, type Command, type EditorCover, PANEL_INSET, WORKSPACE_SCOPE_ATTR, WORKSPACE_LOADED_ATTR, WORKSPACE_HREF_ATTR } from '@papan/core-ui/ui'
import { usePageTitle } from '@papan/brand/react'
import { useRealtime } from '@papan/realtime/client'
import { createAccessBurst, changesViewerAccess, damagedMessageFrom, type WorkspaceAccessOutcome } from '@papan/types'
import { filesApi } from '@papan/files/client'
import { fileKeys, fetchAndCacheFileInfo, useIdentitySwitchEpoch } from '@papan/files/client/queries'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { request, pinnedBatch } from '@papan/api-client'
import { workspaceApi, loadWorkspaceAccess } from '@papan/workspace/client'
import type { RecentWorkspace } from './WorkspaceMenuBar'
import { collectDroppedFiles, processCollectedDrop, uploadFilesAsBatch } from '@papan/files/client/dropUpload'
import { isPapanDrag, readPapanDrag } from '@papan/core-ui/ui/papanDrag'
import { beforeUnloadPrompt } from '@papan/core-ui/ui/sessionEnded'
import { useNewProject } from '@papan/files/ui'
import { useWorkspace, PLAIN_FOLDER_CONFIG_ERROR } from './useWorkspace'
import { useWorkspaceManager } from './WorkspaceManagerContext'
import { WorkspaceExplorer, type WorkspaceExplorerHandle } from './WorkspaceExplorer'
import { WorkspaceMenuBar, type EditContext } from './WorkspaceMenuBar'
import { TabBar } from './TabBar'
import { FilePickerDialog } from './FilePickerDialog'
import { SaveAsDialog } from './SaveAsDialog'
import { WorkspacePickerDialog } from './WorkspacePickerDialog'
import { TabContent, type EditorEditHandle } from './TabContent'
import { requestMiniappView, peekPendingMiniappView, selectionFingerprint, revealLineRequest, type EditorSelection, type RevealLineRequest } from '@papan/collab/client'
import { isMiniappFolder, stripMiniappFolderExt, stripProjectFolderExt, enforcedAIOverride, canWriteFor, pointerKindOf, isSharedPointer, isChainPointer } from '@papan/types'
import { hasNoAgentUsableLinks } from '@papan/workspace/types'
import type { OpenFileTargetWire, OpenPdappTargetWire, OpenFileOptsWire } from '@papan/collab/ui'
import { UnsavedChangesDialog, type UnsavedAction } from './UnsavedChangesDialog'
import { RelinkDialog } from './RelinkDialog'
import { tabsUnderFolders, tabsUnderDeleted, tabFileIsBack, isFolderPathEvent } from './tab-ancestry'
import { useEndedTabRecovery } from './useEndedTabRecovery'
import { ChatPanel } from './ChatPanel'
import { VersionPanel } from './VersionPanel'
import { AppVersionPanel } from './AppVersionPanel'
import { VersionPreview } from './VersionPreview'
import { ResizeHandle } from './ResizeHandle'
import { useSaveBlockedNotice, PREVIEW_SAVE_BLOCKED_KEY } from './useSaveBlockedNotice.js'
import { isVersionPreviewCovering } from './versionPreviewCover.js'

// BreadcrumbPath imported from @papan/core-ui/ui

interface WorkspaceViewProps {
  /** The .workpapan folder ID (NOT the workspace database record ID) */
  folderId: string
  tenantId: string
  tenantName?: string
  isActive: boolean
  userRole?: string
  orgEmail?: string
}

export function WorkspaceView({ folderId, tenantId, tenantName, isActive, userRole, orgEmail }: WorkspaceViewProps) {
  // #1210: an org-email switch reloads version previews as the identity now active (tabs key themselves)
  const identityEpoch = useIdentitySwitchEpoch()
  const { t: tCollab } = useTranslation('collab')
  const { t: tFiles } = useTranslation('files')

  // User permission map: folderId → permission (like aiOverrideMap but for user access)
  // Built from role-based + user-specific folder_permissions
  const [userPermMap, setUserPermMap] = useState<Map<string, string>>(new Map())
  const fetchUserPermMap = useCallback(() => {
    request<{ permissions: Array<{ folderId: string; permission: string }>; rolePermissions: Array<{ folderId: string; permission: string }> }>(
      '/api/folders/user/me/permissions'
    ).then(data => {
      const map = new Map<string, string>()
      // Role-based first (lower priority)
      for (const p of data.rolePermissions) map.set(p.folderId, p.permission)
      // User-specific overrides (higher priority)
      for (const p of data.permissions) map.set(p.folderId, p.permission)
      setUserPermMap(map)
    }).catch(() => {})
  }, [])
  useEffect(() => { fetchUserPermMap() }, [userRole, fetchUserPermMap]) // re-fetch on role change

  // Re-fetch userPermMap + rebuild file tree + re-check workspace permission on access_changed
  const [accessRefreshKey, setAccessRefreshKey] = useState(0)
  /** Bumped on an access reaction that names no one: ended tabs re-ask, keeping the refusal guard. */
  const [endedRecheckKey, setEndedRecheckKey] = useState(0)
  const [liveWsPerm, setLiveWsPerm] = useState<string | null>(null)
  const { onOrgEvent, onPermissionChanged } = useRealtime()
  const queryClient = useQueryClient()
  // A cached workspace that isn't on screen waits until it is shown (#1259).
  const whenShown = useWhenShown()
  useEffect(() => {
    // At most once per interval (`createAccessBurst`): every member's tab gets each grant /
    // team edit, and each reaction here refetches the permission map, the
    // explorer root, the linked trees and the workspace's permission — an
    // admin's run of team edits flooded the read limit (gnexis 2026-10-21).
    // The permission + list caches are invalidated (at most once per interval) by
    // FileListCacheSync, not here (it ran once per cached workspace).
    // #1259: a change naming ANOTHER member is skipped (`changesViewerAccess`); a hidden cached
    // workspace reacts once when shown (`whenShown`); the folder's permission is read with
    // every other one asked at that moment (`loadPermission`).
    // A permission:changed naming NO one (a share link minted, an org share changed — every member
    // gets those) re-checks the workspace folder's own permission (in the shared batch) and the
    // ENDED tabs (another org restoring its share brings them back — it publishes only that); the
    // permission map and the explorer bump are for a change that is this viewer's (an org access /
    // role change, or one naming them). The explorer reloads its loaded folders on every such
    // signal by itself (its permBurst — app reach, I116), as before this branch.
    let full = false
    const burst = createAccessBurst(() => whenShown(() => {
      if (full) {
        full = false
        fetchUserPermMap()
        setAccessRefreshKey(k => k + 1)
      } else {
        setEndedRecheckKey(k => k + 1)
      }
      // Re-check workspace folder permission (handles both upgrade and revoke). #1260: only a real
      // revoke (403 / 404, a hidden shell) closes it — a 429 or a network failure is a failed
      // CHECK, and closing on it threw people out of their workspace during a read flood.
      const close = () => {
        wsManagerRef.current?.closeWorkspace(folderId)
        const prefix = tenantId.startsWith('p_') ? '/~' : `/t/${tenantId}`
        navigateRef.current?.(`${prefix}/folder`)
      }
      const apply = (outcome: WorkspaceAccessOutcome) => {
        if (outcome.kind === 'leave') close()
        else if (outcome.kind === 'permission') setLiveWsPerm(outcome.permission)
      }
      // as of NOW: an answer asked before a later change is superseded (`null`) — that change's own
      // reaction asks again (#1260, Codex review: a 403 read before a re-grant closed the workspace)
      // …and (#1406) whether it still opens as a project — the ONE re-check (`loadWorkspaceAccess`)
      loadWorkspaceAccess(folderId).then((outcome) => { if (outcome) apply(outcome) })
    }))
    const off = onOrgEvent((event) => {
      if ((event.type === 'org:access_changed' || event.type === 'org:role_changed') && changesViewerAccess(event)) { full = true; burst.push() }
    })
    // …and on permission:changed: some changes publish only that (a platform admin setting a
    // support seat's role, an app's reach) — Codex review.
    const offPerm = onPermissionChanged((event) => {
      if (!changesViewerAccess(event)) return
      if (event.affectsViewer === true) full = true
      burst.push()
    })
    // Dropping a pending reaction here loses nothing: `folderId` never changes inside one instance
    // (WorkspaceCache keys each workspace on it), so this only runs on unmount.
    return () => { off(); offPerm(); burst.cancel() }
  }, [onOrgEvent, onPermissionChanged, fetchUserPermMap, folderId, whenShown])
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  const explorerRef = useRef<WorkspaceExplorerHandle>(null)
  const explorerContainerRef = useRef<HTMLDivElement>(null)
  const editorContainerRef = useRef<HTMLDivElement>(null)
  const panelsContainerRef = useRef<HTMLDivElement>(null)

  // Minimum widths for the 3 panels
  const MIN_EXPLORER = 150
  const MIN_EDITOR = 200
  const MIN_CHAT = 250
  // Default widths a collapsed side panel restores to (restore tab / double-click).
  const DEFAULT_EXPLORER = 220
  const DEFAULT_CHAT = 320
  // Past the minimum the panel rubber-bands: an exponential resistance that
  // leaves the min at slope 1 (a seamless continuation of the normal drag —
  // no kink) and stiffens smoothly, asymptoting to STRETCH px of give.
  // Pull SNAP_OVER px past the min and it snaps closed; release before that
  // and it springs back to the min.
  const STRETCH = 72
  const SNAP_OVER = 108
  // Hysteresis for the collapse<->restore toggle DURING a live drag: once the
  // panel has snapped closed (over ≥ SNAP_OVER), pulling back until it's only
  // RESTORE_OVER px past the min reopens it — without releasing the pointer.
  // The gap between the two (108 vs 72) is the dead zone that stops flicker.
  // This threshold also governs the reverse gesture: dragging a *collapsed*
  // panel back out from the page edge, which starts at width 0 (over = MIN)
  // and reveals once it crosses this same point.
  const RESTORE_OVER = 72
  // Fixed chrome: 2 resize handles (w-1.5 = 6px at rest) + the panels' side
  // insets (12px left + 12px right, now margins not row padding) + 4 flex gaps
  // between the 5 row children (4px each). Slightly high when a panel is
  // collapsed (its margin is 0) — harmlessly conservative for the max clamp.
  const CHROME_WIDTH = 52
  // Side inset (drive shell p-3 = 12px) lives on each side panel's outer
  // margin, so it collapses with the panel. When collapsed the handle + its two
  // 4px gaps already supply 14px of edge spacing, so the inner margin gets a
  // −2px trim to land the editor exactly on the 12px line. Both are referenced
  // by beginResize AND the panel JSX below.
  const COLLAPSE_TRIM = -2
  // Unhide gesture: while dragging a collapsed panel's edge back out, the panel
  // stays hidden and the EDITOR budges toward where the panel will land — a
  // pre-snap tell that ramps quartically (near-zero until you're close, then a
  // magnet-like pull). Cross UNHIDE_SNAP px and the panel snaps in; drop back
  // under UNHIDE_HIDE and it re-hides (hysteresis). UNHIDE_BUDGE caps the budge.
  const UNHIDE_SNAP = 72
  const UNHIDE_HIDE = 44
  const UNHIDE_BUDGE = 24
  const [editContext, setEditContext] = useState<EditContext>('explorer')
  // Tab overview sheet open — lifts the editor card above its sibling
  // cards so the sheet's fixed scrim (inside this card's stacking
  // context) can cover them.
  const [tabOverviewOpen, setTabOverviewOpen] = useState(false)
  const [editorDropActive, setEditorDropActive] = useState(false)
  const editorDragCounter = useRef(0)
  // Pending file to activate after upload-from-editor-drop
  const [pendingActiveFileId, setPendingActiveFileId] = useState<string | null>(null)
  // File picker dialog visibility
  const [showFilePicker, setShowFilePicker] = useState(false)
  // Save As dialog state
  const [saveAsTarget, setSaveAsTarget] = useState<{ fileId: string; name: string; parentId: string | null } | null>(null)
  // Export-to-drive dialog state (for editor PDF export etc.)
  const [exportTarget, setExportTarget] = useState<{ blob: Blob; name: string; parentId: string | null; title?: string } | null>(null)
  // Add sheet dialog state (reuses SaveAsDialog for CSV workbook groups)
  const [addSheetTarget, setAddSheetTarget] = useState<{ defaultName: string; parentId: string | null; sourceFileId?: string } | null>(null)
  // Relink sheet dialog — swap backing CSV for a sheet
  const [relinkTarget, setRelinkTarget] = useState<{ sheetId: string; sheetName: string; parentId: string | null; sourceFileId?: string } | null>(null)
  // Ref captures last add-sheet context so merge prompt handler can access it after dialog closes
  const lastAddSheetContextRef = useRef<{ parentId: string | null; sourceFileId?: string } | null>(null)
  // Relink dialog — shown when missing links detected
  const [showRelinkDialog, setShowRelinkDialog] = useState(false)
  const originalMissingLinksRef = useRef<typeof ws.missingLinks>([])

  // Folder picker for relinking — stores resolve callback
  const relinkResolveRef = useRef<((result: { id: string; name: string } | null) => void) | null>(null)
  const [showRelinkPicker, setShowRelinkPicker] = useState(false)
  const [relinkPickerHint, setRelinkPickerHint] = useState<string | undefined>()

  // Lazy-mount ChatPanel: mount on first activation, keep mounted forever after
  const [chatMounted, setChatMounted] = useState(isActive)
  useEffect(() => { if (isActive) setChatMounted(true) }, [isActive])


  // Version history panel — tracks which file's history is open (null = closed)
  const [versionPanelFileId, setVersionPanelFileId] = useState<string | null>(null)
  // App-level version history (#612 Task 10) — the `.pdapp` folder's own
  // history, a sibling of the per-file panel above and mutually exclusive
  // with it (same side-panel slot). Only offered when this workspace's
  // container IS a pdapp folder (`ws.entityKind === 'miniapp'`).
  const [showAppVersionPanel, setShowAppVersionPanel] = useState(false)
  // Version preview — when set, overlays editor area with read-only preview
  const [previewVersionId, setPreviewVersionId] = useState<string | null>(null)
  const [previewIsLatest, setPreviewIsLatest] = useState(false)
  // App-version preview (#612) — the app's main HTML (`index.html`) as it was
  // at a selected app-version, shown read-only in the same overlay slot as the
  // per-file preview. Reuses VersionPreview with its per-file restore hidden
  // (the whole-app restore lives on the App history panel's own bar).
  const [appPreview, setAppPreview] = useState<{ fileId: string; versionId: string; isLatest: boolean; versionNumber: number } | null>(null)
  /**
   * A VERSION PREVIEW is covering the editor — the per-file one or the PDApp
   * one, and nothing else.
   *
   * The two halves are EXACTLY those two overlays' render conditions below,
   * written once so the guard and the overlay cannot drift apart. Both are
   * `absolute inset-0 z-10` over a still-mounted, still-editable live editor,
   * and both used to leave that editor owning the keyboard.
   *
   * NAMED FOR WHAT IT COVERS, not "an overlay is open", because it is NOT
   * every cover. The conflict "Saved version" panel hides the live editor with
   * `display:none` and shows a read-only one in its place; those editors guard
   * their own UNDO (`useUndoScopeGuard` in CollabDocEditor and friends) and
   * REPORT the cover themselves (`useCoverEditor`, #930) — this flag stays
   * false there. Every Save gate below reads `editorCovered`, which is this
   * flag OR the active tab's reported cover. See EditorCover.
   */
  const versionPreviewOpen = isVersionPreviewCovering({ previewVersionId, versionPanelFileId, appPreview })
  /** A ref because the Cmd+S listener's deps do not name the preview — the
   *  handler would otherwise close over the value it had when it subscribed. */
  const versionPreviewOpenRef = useRef(versionPreviewOpen)
  versionPreviewOpenRef.current = versionPreviewOpen

  // Per-file editor view mode (shared between editor and version preview)
  const editorViewModesRef = useRef<Record<string, 'preview' | 'source'>>({})
  const [, setEditorViewModeVersion] = useState(0) // trigger re-render on change
  const getEditorViewMode = useCallback((fileId: string) => editorViewModesRef.current[fileId], [])
  const setEditorViewMode = useCallback((fileId: string, mode: 'preview' | 'source') => {
    editorViewModesRef.current[fileId] = mode
    setEditorViewModeVersion(v => v + 1)
  }, [])
  // Per-file scroll position (shared between editor and version preview)
  const editorScrollRef = useRef<Record<string, number>>({})  // legacy
  const viewSyncRef = useRef<Record<string, Record<string, unknown>>>({})
  // Increment to notify live editor to re-read sync state (e.g., after preview closes)
  const [scrollSyncTrigger, setScrollSyncTrigger] = useState(0)
  const closePreview = useCallback(() => {
    setPreviewVersionId(null)
    setAppPreview(null)
    setScrollSyncTrigger(v => v + 1)
  }, [])

  // Per-tab refs: save functions, discard functions, and editor handles (for edit menu actions)
  const tabSaveRefs = useRef(new Map<string, React.MutableRefObject<(() => void) | null>>())
  const tabDiscardRefs = useRef(new Map<string, React.MutableRefObject<(() => void) | null>>())
  const tabEditorRefs = useRef(new Map<string, React.RefObject<EditorEditHandle | null>>())
  // Per-tab stable dirty-change callbacks (so editor init effects don't re-run)
  const tabDirtyHandlers = useRef(new Map<string, (dirty: boolean) => void>())

  // folderId is always defined (never empty — empty flow is in EmptyWorkspaceView)
  const ws = useWorkspace({ folderId, isActive })
  // #930 — a tab whose editor covers itself (the conflict "Saved version" panel) reports it here; see EditorCover.
  const [tabCovers, setTabCovers] = useState<Record<string, EditorCover>>({})
  /** The ACTIVE tab's own cover — what Save refuses under, with its own notice. */
  const activeCover = ws.activeTabId ? tabCovers[ws.activeTabId] ?? null : null
  const activeCoverRef = useRef(activeCover)
  activeCoverRef.current = activeCover
  /**
   * SOMETHING covers the active editor: a version preview, or its own reported cover. What every Save gate reads —
   * Cmd+S, the File menu, the tab menu, `file.*` — so the hidden document is never written. One flag, so a new gate
   * can't pick the narrower one.
   */
  const editorCovered = versionPreviewOpen || activeCover !== null
  // The routes read this flag to disable themselves and say why; `handleSaveTab` / `handleDiscardTab` / `handleSaveAll`
  // refuse a covered tab whatever the route (the backstop). Not by wrapping the per-tab save refs: callers read an
  // empty save ref as "this editor cannot save", so a guard installed there would change that meaning.
  /** Tabs whose editor covers itself — the tab menu refuses THAT tab's Save / Save As / Discard. */
  const coveredTabIds = useMemo(() => new Set(Object.keys(tabCovers)), [tabCovers])
  /**
   * Save All — ONE rule for every route (File menu, tab menu, `file.saveAll`): a version preview refuses it (as it
   * always has), an editor's own cover does not — `handleSaveAll` skips the covered tabs and the rest still save. So
   * it is offered only when a NON-covered tab is dirty.
   */
  const saveAllAvailable = !versionPreviewOpen && ws.tabs.some(t => t.isDirty && !isTabCovered(t.id, tabCovers, false))
  // Name the tab after the project/app. `isActive` gates it because the shell
  // keeps inactive workspace windows mounted — without it, whichever one
  // happened to render last would name a tab it is not showing.
  usePageTitle(stripProjectFolderExt(stripMiniappFolderExt(ws.workspaceName ?? '')), isActive)

  // The PDApp folder id for the ACTIVE tab, when that tab IS a PDApp folder —
  // i.e. an "app view" — for `AppVersionPanel` and the "App history" toggle
  // (#612). "App history" is a property of the APP, so it is offered on any
  // pdapp-folder tab (the workspace's own pdapp AND a nested child `.pdapp`
  // opened as a tab) and scoped to THAT folder; per-file "History" shows on
  // file tabs. Detected from the active tab's PATH (its last segment is a
  // pdapp folder name) so a child pdapp gets its OWN app history — not the
  // parent's, and not a dead per-file "History" on a folder. `null` on file
  // tabs and non-pdapp workspaces. (Recording resolves the NEAREST enclosing
  // pdapp, so a child's files already record under the child's folder.)
  const appTab = useMemo(() => {
    const seg = ws.activeTab?.path?.split('/').filter(Boolean).pop() ?? ''
    return ws.activeTab && isMiniappFolder(seg) ? { id: ws.activeTab.fileId, name: seg } : null
  }, [ws.activeTab])
  const isAppTab = !!appTab

  // (#327 Track B v1) The assistant stays OFF a MOUNT-ROOTED workspace: its sandbox
  // would bind cross-tenant SOURCE content, the one piece Track B defers. The worker
  // already suppresses those mounts, but leaving the panel up would still advertise
  // the links as "Read & Write" — on a grant that may be read-only — and invite the
  // user to ask for work the agent cannot see. `isMount` is server-set in buildConfig;
  // a link carries only an id, so the client cannot infer it.
  // (#327 B3) Hide the assistant only when there would be NOTHING for it to work
  // on — every link is a mount the worker will refuse to bind.
  //
  // Not `.some()`. That suppressed the whole workspace on ANY dead mount, so a
  // workspace of native folders plus one revoked `.shared` lost its assistant
  // entirely, even though the worker drops the dead mount and binds the rest.
  // The `hidden`-placement variant was worse: an org carving ONE member out of a
  // mount linked into a shared team workspace cost that member their assistant
  // while colleagues kept theirs, with nothing on screen explaining why.
  //
  // That was the exact mirror of the bug this flag was added to fix — advertising
  // an assistant that cannot work, versus hiding one that can.
  //
  // `agentCapable` is server-set: a link carries only an id, so the client can
  // read neither the kind nor the grant. Both flags absent (older server,
  // un-threaded route) → the link counts as unusable, which keeps the
  // fail-quiet direction for a pure mount-rooted workspace.
  const isMountRootedWs = ws.configLoaded && hasNoAgentUsableLinks(ws.links)

  // Per-panel collapse — persisted via useWorkspace. Aliased so the rest of
  // the view reads them plainly.
  const explorerCollapsed = ws.explorerCollapsed
  const chatCollapsed = ws.chatCollapsed
  const setExplorerCollapsed = ws.setExplorerCollapsed
  const setChatCollapsed = ws.setChatCollapsed

  // Close workspace if user has no access (detected on load or after config refresh)
  // Only navigate if this workspace is active — hidden (cached) workspaces should not steal focus
  useEffect(() => {
    if (ws.configError === 'no_access') {
      wsManagerRef.current?.closeWorkspace(folderId)
      if (isActive) {
        const prefix = tenantId.startsWith('p_') ? '/~' : `/t/${tenantId}`
        navigateRef.current?.(`${prefix}/folder`)
      }
    }
    // #1406 I3: no longer a project to this person, but still a folder their drive opens — browse it
    if (ws.configError === PLAIN_FOLDER_CONFIG_ERROR) {
      wsManagerRef.current?.closeWorkspace(folderId)
      if (isActive) {
        const prefix = tenantId.startsWith('p_') ? '/~' : `/t/${tenantId}`
        navigateRef.current?.(`${prefix}/folder/${folderId}`, { replace: true })
      }
    }
  }, [ws.configError, folderId, tenantId, isActive])

  // Effective permissions: live permission (from access_changed) > callerPermission (from load).
  // #872: the permission resolved on the workspace folder alone decides — never the org role
  // (an Edit grant lets a Viewer write); unknown (still loading) allows nothing.
  const wsPerm = liveWsPerm || ws.callerPermission
  const canWrite = canWriteFor(wsPerm)
  const canCopy = !!wsPerm && ['admin', 'readwrite', 'readcopy', 'owner'].includes(wsPerm)

  const wsManager = useWorkspaceManager()
  const wsManagerRef = useRef(wsManager)
  wsManagerRef.current = wsManager
  const toast = useToast()
  const noticeSaveBlocked = useSaveBlockedNotice(toast, versionPreviewOpen ? 'preview' : activeCover)
  /**
   * The ACTIVE tab's dirtiness, not "any tab is dirty" — the only reachable
   * branch of the Cmd+S handler saves the active tab, so "any" promised a save
   * the keystroke cannot perform: tab A clean under a preview, tab B dirty in
   * the background, Cmd+S says "close the preview to save", the user closes it,
   * presses again, and nothing is written. Same predicate as the File menu's
   * Save, minus the preview term.
   *
   * A ref because this boolean also depends on `ws.activeTabId`, and that is its
   * own `useState` — switching tabs changes the answer while leaving `ws.tabs`
   * identity untouched, so the listener is NOT re-bound and a closed-over value
   * would be stale.
   *
   * It takes a SECOND preview to read that stale value, because a tab switch
   * closes any open one (the effect below, keyed on `ws.activeTabId`): tab A
   * dirty, tab B clean, switch to B, open B's version history and preview a
   * version, press Cmd+S — a closure would still hold A's `true` and offer
   * "Close the version preview to save" for a file with nothing to save. The
   * other direction is just as reachable and worse: A clean and active, B goes
   * dirty, switch to B, preview a version there, Cmd+S — a stale `false` shows
   * no notice at all and the keystroke is swallowed over real unsaved work.
   *
   * (A dirty FLIP does re-bind the listener today — `setTabDirty` returns a new
   * `ws.tabs` array, which re-identifies `handleSaveAll`, which is in the deps.
   * That is incidental, and is not what the ref is for.)
   */
  const hasDirtyRef = useRef(false)
  hasDirtyRef.current = !!ws.tabs.find(t => t.id === ws.activeTabId)?.isDirty
  const resolveConflict = useConflict()
  const { onFileEvent, onResync } = useRealtime()

  // Show relink dialog when missing links detected after config loads
  useEffect(() => {
    if (ws.configLoaded && ws.missingLinks.length > 0) {
      originalMissingLinksRef.current = [...ws.missingLinks]
      setShowRelinkDialog(true)
    }
  }, [ws.configLoaded, ws.missingLinks.length])

  // #939: a FOLDER renamed or moved changes the path of every open file under it. Collect
  // those folders for a moment, then refresh only the tabs whose path runs through one.
  const movedFoldersRef = useRef<Set<string>>(new Set())
  const movedFoldersTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tabsRef = useRef(ws.tabs)
  tabsRef.current = ws.tabs
  useEffect(() => () => { if (movedFoldersTimerRef.current) clearTimeout(movedFoldersTimerRef.current) }, [])

  // #823: the same collect-then-match for deletes. A delete event names only the deleted ROOT, so a binned parent
  // folder reaches the tabs under it through their cached breadcrumbs; a tab whose breadcrumbs can't be trusted asks
  // the server whether its file is still there (403 / 404 = gone with it). Those tabs are marked deleted — greyed,
  // kept open so the last content can be read and copied; a live editor also shows the server's own notice. (A delete
  // made from this window's drive closes its tabs itself, before the event arrives.)
  const UNSURE_TAB_CHECK_BATCH = 10
  /** #823: is a greyed tab's file back (restored)? A pointer row's target decides (`tabFileIsBack`). */
  const fileIsBack = useCallback((fileId: string) => tabFileIsBack(fileId, id => filesApi.getFileInfo(id)), [])
  const deletedRef = useRef<Set<string>>(new Set())
  const deletedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => { if (deletedTimerRef.current) clearTimeout(deletedTimerRef.current) }, [])
  const setTabsDeletedRef = useRef(ws.setTabsDeleted)
  setTabsDeletedRef.current = ws.setTabsDeleted
  const markTabsUnderDeleted = useCallback(() => {
    deletedTimerRef.current = null
    const ids = deletedRef.current
    deletedRef.current = new Set()
    // tabs already greyed are settled; the rest ask at most a bounded number of times per burst of deletes
    const open = tabsRef.current.filter(t => !t.deleted).map(t => ({ fileId: t.fileId, path: t.path }))
    const { under, unsure } = tabsUnderDeleted(open, ids,
      id => queryClient.getQueryData<Array<{ id: string; name?: string }>>(fileKeys.breadcrumbs(id)))
    setTabsDeletedRef.current(under, true)
    // (the detail answers 403 for "gone" and "not yours" alike, #630 — either way the tab can't show it any more).
    // A few at a time, all of them in the end: a burst of deletes must not fire one request per tab at once.
    void (async () => {
      for (let i = 0; i < unsure.length; i += UNSURE_TAB_CHECK_BATCH) {
        await Promise.all(unsure.slice(i, i + UNSURE_TAB_CHECK_BATCH).map(id => filesApi.getFileInfo(id).catch((err: unknown) => {
          const status = (err as { status?: number } | null)?.status
          // greyed by an answer that may predate a restore that already passed — ask once more
          if (status === 403 || status === 404) { setTabsDeletedRef.current([id], true); askEndedTabRef.current(id) }
        })))
      }
    })()
  }, [queryClient])

  // #823: an editor whose session the server ended greys its tab (or, refused, is remembered) and comes back only on
  // the server's "back" answer — at the end, on every restore, on every resync (`useEndedTabRecovery`)
  const deleteTouches = useCallback((fileId: string, deletedId: string) => {
    if (fileId === deletedId) return true
    // a pointer's breadcrumbs are its own path, not its target's: any delete may be its target's
    const detail = queryClient.getQueryData<{ fileType?: string | null; name?: string }>(fileKeys.detail(fileId))
    // (a tab restored offline carries its file id as its title — both names are asked)
    const names = [tabsRef.current.find(t => t.fileId === fileId)?.title ?? '', detail?.name ?? '']
    if (pointerKindOf(detail) || names.some(n => isSharedPointer(n) || isChainPointer(n))) return true
    const crumbs = queryClient.getQueryData<Array<{ id: string }>>(fileKeys.breadcrumbs(fileId))
    // unsure (no breadcrumbs cached) counts as reached: the answer is asked again, never wrongly kept
    return !crumbs || crumbs.some(b => b.id === deletedId || (b.id.startsWith('s_') && b.id.endsWith(`_${deletedId}`)))
  }, [queryClient])
  const endedTabs = useEndedTabRecovery({
    tabsRef,
    setTabsDeleted: ws.setTabsDeleted,
    remountTabs: ws.remountTabs,
    fileIsBack,
    deleteTouches,
    accessRefreshKey,
    endedRecheckKey,
    onFileEvent,
    onResync,
  })
  const askEndedTabRef = useRef(endedTabs.ask)
  askEndedTabRef.current = endedTabs.ask

  // Refresh tab metadata (title, type) when files are renamed;
  // close tabs when their file (or parent folder) is deleted
  useEffect(() => {
    return onFileEvent((event) => {
      if (isFolderPathEvent(event)) {
        movedFoldersRef.current.add(event.fileId)
        if (movedFoldersTimerRef.current) clearTimeout(movedFoldersTimerRef.current)
        movedFoldersTimerRef.current = setTimeout(() => {
          movedFoldersTimerRef.current = null
          const folders = movedFoldersRef.current
          movedFoldersRef.current = new Set()
          const fileIds = tabsUnderFolders(tabsRef.current.map(t => ({ fileId: t.fileId, path: t.path })), folders,
            id => queryClient.getQueryData<Array<{ id: string; name?: string }>>(fileKeys.breadcrumbs(id)))
          for (const id of fileIds) ws.refreshTabMeta(id)
        }, 500)
      }
      if (event.type === 'file:moved') {
        // #662: a SYNC-DETECTED agent `mv` mints a NEW drive row, so the event
        // carries `previousFileId` (the OLD, now-binned id) and a different
        // `fileId`. A tab open on the old id can't be refreshed to a live row
        // (its id no longer exists) — close it, exactly as the pre-#662
        // `file:deleted(oldId)` did, so it doesn't linger editing a binned row.
        // A same-id move (drive-UI drag) has no `previousFileId` → just refresh.
        if (event.previousFileId && event.previousFileId !== event.fileId) {
          const tab = ws.tabs.find(t => t.fileId === event.previousFileId)
          if (tab) ws.closeTab(tab.id)
        } else {
          ws.refreshTabMeta(event.fileId)
        }
      } else if (event.type === 'file:deleted') {
        // the tab ON the deleted item greys NOW — the cache patch drops its detail in this same dispatch, and its
        // editor must keep the last one (EditorHost `deleted`); only "under a deleted folder" needs the walk below
        if (tabsRef.current.some(t => t.fileId === event.fileId)) setTabsDeletedRef.current([event.fileId], true)
        deletedRef.current.add(event.fileId)
        if (deletedTimerRef.current) clearTimeout(deletedTimerRef.current)
        deletedTimerRef.current = setTimeout(markTabsUnderDeleted, 1_000)
      } else if (event.type === 'file:restored') {
        // a delete still waiting for the walk (binned and restored within its debounce) no longer counts; the greyed
        // tabs ask the server whether they are back (`useEndedTabRecovery`) — the event alone un-greys nothing
        deletedRef.current.delete(event.fileId)
      }
    })
  }, [onFileEvent, ws.refreshTabMeta, ws.tabs, ws.closeTab, ws.setTabsDeleted, markTabsUnderDeleted, queryClient])

  // Helpers to get-or-create per-tab refs (stable across renders)
  const setTabDirtyRef = useRef(ws.setTabDirty)
  setTabDirtyRef.current = ws.setTabDirty
  const setTabConflictedRef = useRef(ws.setTabConflicted)
  setTabConflictedRef.current = ws.setTabConflicted

  function getTabSaveRef(tabId: string): React.MutableRefObject<(() => void) | null> {
    let ref = tabSaveRefs.current.get(tabId)
    if (!ref) { ref = { current: null }; tabSaveRefs.current.set(tabId, ref) }
    return ref
  }
  function getTabDiscardRef(tabId: string): React.MutableRefObject<(() => void) | null> {
    let ref = tabDiscardRefs.current.get(tabId)
    if (!ref) { ref = { current: null }; tabDiscardRefs.current.set(tabId, ref) }
    return ref
  }
  function getTabEditorRef(tabId: string): React.RefObject<EditorEditHandle | null> {
    let ref = tabEditorRefs.current.get(tabId)
    if (!ref) { ref = { current: null }; tabEditorRefs.current.set(tabId, ref) }
    return ref
  }
  function getTabDirtyHandler(tabId: string): (dirty: boolean) => void {
    let handler = tabDirtyHandlers.current.get(tabId)
    if (!handler) {
      handler = (dirty: boolean) => setTabDirtyRef.current(tabId, dirty)
      tabDirtyHandlers.current.set(tabId, handler)
    }
    return handler
  }
  const tabCoverHandlers = useRef(new Map<string, (cover: EditorCover | null) => void>())
  function getTabCoverHandler(tabId: string): (cover: EditorCover | null) => void {
    let handler = tabCoverHandlers.current.get(tabId)
    if (!handler) {
      handler = (cover: EditorCover | null) => setTabCovers((prev) => {
        if (cover) return { ...prev, [tabId]: cover }
        if (!(tabId in prev)) return prev
        const { [tabId]: _gone, ...rest } = prev
        return rest
      })
      tabCoverHandlers.current.set(tabId, handler)
    }
    return handler
  }
  const tabConflictHandlers = useRef(new Map<string, (conflicted: boolean) => void>())
  function getTabConflictHandler(tabId: string): (conflicted: boolean) => void {
    let handler = tabConflictHandlers.current.get(tabId)
    if (!handler) {
      handler = (conflicted: boolean) => setTabConflictedRef.current(tabId, conflicted)
      tabConflictHandlers.current.set(tabId, handler)
    }
    return handler
  }

  // Clean up refs for closed tabs
  useEffect(() => {
    const tabIds = new Set(ws.tabs.map(t => t.id))
    for (const id of tabSaveRefs.current.keys()) {
      if (!tabIds.has(id)) tabSaveRefs.current.delete(id)
    }
    for (const id of tabDiscardRefs.current.keys()) {
      if (!tabIds.has(id)) tabDiscardRefs.current.delete(id)
    }
    for (const id of tabEditorRefs.current.keys()) {
      if (!tabIds.has(id)) tabEditorRefs.current.delete(id)
    }
    for (const id of tabDirtyHandlers.current.keys()) {
      if (!tabIds.has(id)) tabDirtyHandlers.current.delete(id)
    }
    for (const id of tabCoverHandlers.current.keys()) {
      if (!tabIds.has(id)) tabCoverHandlers.current.delete(id)
    }
  }, [ws.tabs])

  // Register with workspace manager when workspace config is loaded
  useEffect(() => {
    if (ws.folderId === folderId && ws.configLoaded && ws.workspaceName && ws.workspaceId) {
      wsManager.openWorkspace({
        id: ws.workspaceId,
        folderId,
        name: ws.workspaceName,
      })
    }
  }, [folderId, ws.folderId, ws.configLoaded, ws.workspaceName]) // eslint-disable-line react-hooks/exhaustive-deps

  // Open file from URL query param (e.g. navigating from DriveView)
  // The ?open= param now contains a file ID
  useEffect(() => {
    if (!isActive) return
    const openFileId = searchParams.get('open')
    if (openFileId && ws.configLoaded) {
      ws.openFile(openFileId)
      // Consume the one-shot deep-link. Delete ONLY `open` (function form) so we
      // preserve `file`/`fs` instead of nuking the whole query with `{}`. The
      // file-sync effect below ALSO strips `open` (it runs last in the same
      // flush and wins the race); this is the belt-and-suspenders path for when
      // no tab change re-fires that effect.
      setSearchParams(prev => {
        const next = new URLSearchParams(prev)
        next.delete('open')
        return next
      }, { replace: true })
    }
  }, [isActive, ws.configLoaded]) // eslint-disable-line react-hooks/exhaustive-deps

  // A freshly-created (or otherwise tab-less) miniapp workspace should land on
  // the running app, not the empty "open a file" placeholder. Auto-open the
  // MiniAppEditor tab (whose fileId is the .miniapp folder itself) ONCE per
  // mount when no tabs were restored — MiniAppEditor defaults its face to 'run',
  // and we also request 'run' explicitly, so it comes up running. The ref guard
  // means closing that tab doesn't immediately reopen it (no close-trap); a
  // miniapp with saved tabs restores those instead (marks the ref without
  // opening). Scoped to miniapps so project workspaces keep their file landing.
  const autoRanMiniappRef = useRef(false)
  useEffect(() => {
    if (!isActive || !ws.configLoaded || autoRanMiniappRef.current) return
    if (ws.entityKind !== 'miniapp' || !ws.folderId) return
    autoRanMiniappRef.current = true
    if (ws.tabs.length > 0) return
    // Defer to an explicit face already requested from outside (the Drive /
    // app-window wrench sets 'settings' before navigating here) — only apply the
    // 'run' landing default when nothing was requested, so the wrench isn't stomped.
    if (!peekPendingMiniappView(ws.folderId)) requestMiniappView(ws.folderId, 'run')
    ws.openFile(ws.folderId, { name: ws.workspaceName || undefined })
  }, [isActive, ws.configLoaded, ws.entityKind, ws.folderId]) // eslint-disable-line react-hooks/exhaustive-deps

  // `papan:openFile` from sandboxed HTML previews (e.g. shortlist.html in this
  // workspace sends `{type:'papan:openFile', path:'/papan/...'}`).
  //
  // Routed via prop through TabContent → HtmlEditor → HtmlViewer instead of
  // a global `window` message listener. HtmlViewer's existing source gate
  // (its own iframe refs) is the precise check — messages can only come
  // from the iframe instance that this workspace owns, so cross-workspace
  // cross-talk and "any iframe in the document" spoofing are both
  // structurally impossible (no listener for third-party iframes to abuse).
  //
  // Active-workspace guard preserves the prior behavior — inactive workspaces
  // don't navigate from background timers in cached HTML previews.
  // Resolves true only when the file (or folder) was shown IN this workspace — a
  // fullscreen app preview uses that to step aside (HtmlEditor), and stays put
  // when nothing opened here.
  // #249: the line a chat link (`app.py:42`) asked for, per file — the file's tab reveals it.
  const [revealByFile, setRevealByFile] = useState<Record<string, RevealLineRequest>>({})
  const requestReveal = useCallback((fileId: string, line: number | undefined) => {
    if (line && line > 0) setRevealByFile(prev => ({ ...prev, [fileId]: revealLineRequest(line) }))
  }, [])
  // A closed tab's request goes with it: reopening the file later from anywhere else must not jump again.
  const tabFileIdsRef = useRef(new Set<string>())
  useEffect(() => {
    const now = new Set(ws.tabs.map(t => t.fileId))
    const closed = [...tabFileIdsRef.current].filter(id => !now.has(id))
    tabFileIdsRef.current = now
    if (closed.length) setRevealByFile(prev => {
      if (!closed.some(id => id in prev)) return prev
      const next = { ...prev }
      for (const id of closed) delete next[id]
      return next
    })
  }, [ws.tabs])

  const handleOpenFile = useCallback(async (path: string, at?: { line?: number }): Promise<boolean> => {
    if (!isActive) return false
    try {
      const result = await filesApi.resolvePath(path)
      if (!result?.id) {
        toast.error(`File not found: ${path}`)
        return false
      }
      if (result.file.isFolder) {
        return !!(await explorerRef.current?.revealFile(result.id))
      }
      ws.openFile(result.id)
      requestReveal(result.id, at?.line)
      return true
    } catch {
      toast.error(`Could not open: ${path}`)
      return false
    }
  }, [isActive, ws.openFile, toast, requestReveal])

  // #openfile — the typed SDK `openFileBy*` resolver for the in-Papan workspace (SPA) surface.
  // Rows 1–5 of the routing table (same-tenant; `openTenant` cross-tenant → #188):
  //   1 no opts, file ........ open in THIS workspace as a tab
  //   2 no opts, folder in-ctx reveal in the explorer
  //   3 no opts, folder out-ctx drive view in a NEW browser tab (reusable by folderId)
  //   4 workspaceId + access .. that workspace in a NEW tab, reusable by workspaceId
  //   5 workspaceId + no access SingleFileView in a NEW tab, reusable by fileId
  // New tabs use window.open(url, name) — the name makes the tab REUSABLE (same key →
  // same tab, re-navigated). The parent inherits the iframe click's user activation
  // (Chrome propagates it to ancestors), so the open isn't popup-blocked. Native
  // resolution; the app gets nothing back (no oracle).
  // Like handleOpenFile: resolves true only when the target was shown IN this
  // workspace (a tab, or a folder revealed in the explorer); false for a new
  // browser tab, a refusal or a failure.
  /** #788 — `papan.openPdappByFolderId()`: LAUNCH another PDApp. Unlike
   *  `openFile`, there is nothing to resolve here — the target names itself by
   *  id, and the app route resolves and GATES it as the viewer, so a target
   *  they cannot open refuses there. Fire-and-forget: no result, no oracle. A
   *  reusable browser tab keyed by folder id, so repeated launches of the same
   *  app land in one tab (the same keying `openFile` uses). */
  const handleOpenPdappTarget = useCallback((target: OpenPdappTargetWire) => {
    if (!isActive) return
    // ENCODED, like the folderId below it and like App.tsx / MobileAppScreen do.
    // `validateOpenPdappTarget` now shape-checks both ids, so this is the second
    // layer rather than the only one — but an id interpolated raw into a route
    // is the shape that goes wrong when a validator is later relaxed.
    const prefix = target.tenantId === tenantId
      ? (tenantId.startsWith('p_') ? '/~' : `/t/${encodeURIComponent(tenantId)}`)
      : `/t/${encodeURIComponent(target.tenantId)}`
    try {
      window.open(`${prefix}/app/${encodeURIComponent(target.folderId)}`, `papan:app:${target.folderId}`)
    } catch { /* fire-and-forget */ }
  }, [isActive, tenantId])

  const handleOpenFileTarget = useCallback(async (
    target: OpenFileTargetWire,
    ctx: { appFolderId: string | null; opts: OpenFileOptsWire },
  ): Promise<boolean> => {
    if (!isActive) return false
    // `opts` comes from the untrusted app iframe (only `target` is field-validated upstream),
    // so guard the type before it reaches a `window.open` URL — a non-string / metacharacter
    // workspaceId must not inject path/query into the opened route.
    const wantWorkspaceId =
      typeof ctx.opts.workspaceId === 'string' && ctx.opts.workspaceId ? ctx.opts.workspaceId : undefined
    const prefix = tenantId.startsWith('p_') ? '/~' : `/t/${tenantId}`
    try {
      // ── Cross-tenant (openTenant lens) — member-native slice ──────────
      // `openTenant` is an advisory LENS, not a concrete tenant: 'fileTenant'/'auto'
      // (and the default) resolve to the file's owner tenant; 'activeTenant' pins to
      // the current one; 'linkTenant' is an explicit share-link grant. A foreign
      // tenant can only be NAMED by a `byId` target's `fileTenantId` — paths are
      // always active-tenant-relative.
      const otOpt = ctx.opts.openTenant
      if (otOpt === 'linkTenant') {
        // Through a share-link grant = non-native → the share-view path (tenant/email
        // pickers), which rides the reshare branch (deferred #188).
        toast.error('Opening through a share link needs the shared view (coming soon)')
        return false
      }
      const crossTenant =
        otOpt !== 'activeTenant' && target.kind === 'fileId' && target.fileTenantId !== tenantId
          ? { tenant: target.fileTenantId, fileId: target.fileId }
          : null
      if (crossTenant) {
        // Member of the file's tenant? (listMyTenants). Non-member = link-only access
        // → the share-view path (deferred #188). Member → open it there NATIVELY.
        let isMember = false
        try {
          const { tenants } = await request<{ tenants: Array<{ id: string }> }>('/api/orgs/my-tenants')
          isMember = tenants.some(t => t.id === crossTenant.tenant)
        } catch { isMember = false }
        if (!isMember) {
          toast.error('Opening in that tenant needs the shared view (coming soon)')
          return false
        }
        const otPrefix = `/t/${crossTenant.tenant}`
        if (wantWorkspaceId) {
          window.open(`${otPrefix}/workspace/${encodeURIComponent(wantWorkspaceId)}?file=${encodeURIComponent(crossTenant.fileId)}`, `papan:ws:${wantWorkspaceId}`)
        } else {
          window.open(`${otPrefix}/file/${encodeURIComponent(crossTenant.fileId)}`, `papan:file:${crossTenant.fileId}`)
        }
        return false
      }

      // Resolve target → fileId (same-tenant). fileId is direct; paths resolve native.
      let fileId: string
      let isFolder = false
      if (target.kind === 'fileId') {
        fileId = target.fileId
        // #788 — a `.pdapp` FOLDER id must open the FOLDER here, as it does on
        // mobile and through a share link. Before, this branch never asked
        // whether the id was a folder, so `openFileById(<.pdapp>)` LAUNCHED the
        // app on desktop alone. That accident was also the only way to launch
        // an app by id, which is why `openPdappByFolderId` ships with this fix
        // rather than after it.
        // `null` = could not tell even after a retry. Falling back to "file" keeps
        // the recoverable direction (a file view of a folder errors; a wrong
        // launch RUNS someone's app) — see `isFolderById`.
        isFolder = (await filesApi.isFolderById(fileId)) ?? false
      } else {
        const result = target.kind === 'relativePath'
          ? await filesApi.resolvePath(target.path, ctx.appFolderId ?? undefined)
          : await filesApi.resolvePath(`/papan/${target.path.replace(/^\/+/, '')}`)
        if (!result?.id) { toast.error('File not found'); return false }
        fileId = result.id
        isFolder = result.file.isFolder
      }

      // Rows 4–5 — `workspaceId` names a DIFFERENT workspace.
      if (wantWorkspaceId && wantWorkspaceId !== folderId) {
        // Does the user have access to that workspace? (own / shared, this tenant.)
        let hasAccess = false
        try {
          const wss = await workspaceApi.list()
          hasAccess = wss.some(w => w.folderId === wantWorkspaceId || w.id === wantWorkspaceId)
        } catch { hasAccess = false }
        if (hasAccess) {
          // Row 4 — open that workspace, reusable browser tab keyed by workspaceId.
          window.open(`${prefix}/workspace/${encodeURIComponent(wantWorkspaceId)}?file=${encodeURIComponent(fileId)}`, `papan:ws:${wantWorkspaceId}`)
        } else {
          // Row 5 — no workspace access → single-file view, reusable by fileId.
          window.open(`${prefix}/file/${encodeURIComponent(fileId)}`, `papan:file:${fileId}`)
        }
        return false
      }

      // Rows 1–3 — current workspace / active tenant.
      if (isFolder) {
        // In-context folder (under the workspace root or a linked folder) → reveal it.
        // Out-of-context (revealFile returns false) → drive view in a NEW browser tab,
        // reusable by folderId.
        const revealed = await explorerRef.current?.revealFile(fileId)
        if (!revealed) {
          window.open(`${prefix}/folder/${encodeURIComponent(fileId)}`, `papan:folder:${fileId}`)
          return false
        }
        return true
      } else {
        ws.openFile(fileId)
        // Explicitly reveal — if this file's tab is already the active one
        // (re-opening after collapsing its folder / returning from a Drive
        // view), no tab-change event fires, so the auto-reveal effect wouldn't.
        explorerRef.current?.revealFile(fileId)
        return true
      }
    } catch {
      toast.error('Could not open the requested file')
      return false
    }
  }, [isActive, ws.openFile, toast, folderId, tenantId])

  // Restore active tab from URL ?file= param on load
  useEffect(() => {
    if (!isActive || !ws.configLoaded) return
    const fileId = searchParams.get('file')
    if (fileId) {
      const existingTab = ws.tabs.find(t => t.fileId === fileId)
      if (existingTab) {
        ws.setActiveTabId(existingTab.id)
      } else {
        ws.openFile(fileId)
      }
    }
  }, [isActive, ws.configLoaded]) // eslint-disable-line react-hooks/exhaustive-deps

  // Sync active tab to URL ?file= param. Use the function form so the
  // rest of the query string (e.g. ?fs=1 from the HTML editor) is
  // preserved — passing an object literal would replace the whole search.
  useEffect(() => {
    // Gate on configLoaded so this never runs before the `open=` consumer
    // above (also configLoaded-gated, and textually earlier so it reads `open`
    // first in a shared flush). Without the gate this fired on the pre-load
    // mount and stripped `open` before the deep-link could open it.
    if (!isActive || !ws.configLoaded) return
    const activeTab = ws.tabs.find(t => t.id === ws.activeTabId)
    const newFile = activeTab?.fileId ?? null

    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (newFile) next.set('file', newFile)
      else next.delete('file')
      // `open` is a one-shot deep-link consumed by the effect above. This
      // effect runs last and reads the LIVE location, so it's the authoritative
      // place to drop it — copying `prev` would otherwise re-persist a stale
      // `open=`, and a closed tab would reopen on refresh.
      next.delete('open')
      // Skip a redundant replace-navigation when nothing actually changed.
      return next.toString() === prev.toString() ? prev : next
    }, { replace: true })
  }, [isActive, ws.configLoaded, ws.activeTabId, ws.tabs]) // eslint-disable-line react-hooks/exhaustive-deps

  // Reveal active file in explorer — on tab switch AND whenever the workspace
  // view (re)gains focus. The user may have collapsed the file's folder or
  // navigated away to a Drive view; returning to the workspace must re-reveal
  // the active file even though its tab id never changed.
  useEffect(() => {
    const fileId = ws.activeTab?.fileId ?? null
    // Deps fire this only on real events: tab switch (activeTabId), file swap
    // in a tab (fileId), or the workspace regaining focus (isActive false→true)
    // — never on scroll, so this won't yank a tree the user scrolled away from.
    if (fileId && isActive) {
      explorerRef.current?.revealFile(fileId)
    }
  }, [isActive, ws.activeTabId, ws.activeTab?.fileId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Cross-feature reveal: PreviewConsentBanner (Phase 2 preview-sandbox)
  // dispatches `papan:reveal-folder` when the user clicks the proposed-root
  // badge. Forward to the explorer's revealFile, which scrolls + selects +
  // expands ancestors. Window event avoids prop drilling through TabContent.
  useEffect(() => {
    const handler = (e: Event) => {
      const folderId = (e as CustomEvent<{ folderId?: string }>).detail?.folderId
      if (typeof folderId === 'string' && folderId) {
        explorerRef.current?.revealFile(folderId)
      }
    }
    window.addEventListener('papan:reveal-folder', handler)
    return () => window.removeEventListener('papan:reveal-folder', handler)
  }, [])

  // Close version history panel and preview when switching tabs
  useEffect(() => {
    setVersionPanelFileId(null)
    setShowAppVersionPanel(false)
    closePreview()
  }, [ws.activeTabId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Activate tab after editor-area drop upload (deferred until tab exists in state)
  useEffect(() => {
    if (!pendingActiveFileId) return
    const tab = ws.tabs.find(t => t.fileId === pendingActiveFileId)
    if (tab) {
      ws.setActiveTabId(tab.id)
      setPendingActiveFileId(null)
    }
  }, [ws.tabs, pendingActiveFileId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Ctrl+S / Cmd+S to save — only for the active workspace
  const activeTabIdRef = useRef(ws.activeTabId)
  activeTabIdRef.current = ws.activeTabId

  // ─── Editor context tracking (active tab + selection) ──────────────
  // Active tab info — derived from workspace state
  const activeTabInfo = useMemo(() => {
    if (!ws.activeTab) return null
    if (enforcedAIOverride(ws.aiOverrideMap?.get(ws.activeTab.fileId)) === 'hidden') return null
    return { fileId: ws.activeTab.fileId, fileName: ws.activeTab.title, filePath: ws.activeTab.path, fileType: ws.activeTab.type }
  }, [ws.activeTab, ws.aiOverrideMap])

  // Selection is stored in a ref (not state) to avoid re-rendering the editor area.
  // Re-rendering editors causes the browser to reset the textarea selection.
  // ChatPanel subscribes to changes via the 'papan:selection-update' custom event.
  const editorSelectionRef = useRef<EditorSelection | null>(null)
  const selectionTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const chatContainerRef = useRef<HTMLDivElement>(null)
  const lastSelectionKeyRef = useRef('')
  // When true, the entire context chip is hidden (user clicked X).
  // Resets on tab change or new selection activity.
  const contextMutedRef = useRef(false)

  // Reset on tab change
  useEffect(() => {
    editorSelectionRef.current = null
    contextMutedRef.current = false
    lastSelectionKeyRef.current = ''
    window.dispatchEvent(new Event('papan:selection-update'))
    // A kept-mounted tab (desktop shell hides with display:none, never unmounts) still
    // holds its selection, but its editor won't RE-EMIT on activation — the OnlyOffice
    // plugin dedups an unchanged selection, so switching away and back would leave the
    // chip file-only until the selection next changes (#617 finding 5). Re-fire the
    // handler's own (non-focus-gated) event a tick later, after activeTabIdRef has caught
    // up, so it re-pulls the now-active tab's selection through the existing path (hidden
    // check, filePath fill, fingerprint all reused — no duplicated logic).
    const t = setTimeout(() => window.dispatchEvent(new Event('papan:editor-selection-change')), 0)
    return () => clearTimeout(t)
  }, [ws.activeTabId])

  // Drop a cached selection the moment its file becomes "Hidden from AI". Toggling hidden
  // fires NO selection event, so a selection cached before the toggle would otherwise persist
  // and still be sent (the send path gates only on contextMuted; the chip hides with the tab,
  // making the leak invisible). This complements the in-handler clear for the select-while-
  // hidden case. #617 hidden-from-AI bypass.
  useEffect(() => {
    const fid = ws.activeTab?.fileId
    if (fid && enforcedAIOverride(ws.aiOverrideMap?.get(fid)) === 'hidden' && editorSelectionRef.current) {
      editorSelectionRef.current = null
      lastSelectionKeyRef.current = ''
      window.dispatchEvent(new Event('papan:selection-update'))
    }
  }, [ws.aiOverrideMap, ws.activeTab])

  // Listen for selectionchange — debounced, updates ref (no re-render)
  useEffect(() => {
    if (!isActive) return

    const handleSelectionChange = (focusGated: boolean) => {
      clearTimeout(selectionTimerRef.current)
      selectionTimerRef.current = setTimeout(() => {
        // A document `selectionchange` only counts while focus is inside the editor
        // area (clicking elsewhere preserves the last snapshot). But an editor-dispatched
        // `papan:editor-selection-change` (an OnlyOffice cell pick, or an HTML design
        // element pick) originates in a CROSS-ORIGIN iframe whose focus the container
        // check can miss, so it must NOT be focus-gated — otherwise the cell chip
        // silently never appears in project chat. Mirrors ChatHomeView's split.
        if (focusGated && !editorContainerRef.current?.contains(document.activeElement)) return

        const tabId = activeTabIdRef.current
        if (!tabId) return

        const handle = tabEditorRefs.current.get(tabId)?.current
        if (!handle?.getSelection) return

        const sel = handle.getSelection()

        // Fingerprint to avoid redundant updates — the ONE shared spelling (includes
        // sheetName + full text content, not just length, so an A1->B2 move between
        // equal-length values or an in-place edit isn't wrongly deduped). #617 finding 4.
        const key = selectionFingerprint(sel)
        if (key === lastSelectionKeyRef.current) return
        lastSelectionKeyRef.current = key

        if (!sel) {
          editorSelectionRef.current = null
          window.dispatchEvent(new Event('papan:selection-update'))
          return
        }

        // New selection unmutes the chip
        contextMutedRef.current = false

        // Fill in filePath from tab data. Editors that resolved their own
        // path keep it (design-mode element selections point at the file
        // the element was SERVED from — a sub-page, not the entry tab).
        const tab = ws.tabs.find(t => t.id === tabId)
        if (tab) {
          if (!sel.filePath) sel.filePath = tab.path
          // A file "Hidden from AI" must never expose its SELECTION either. CLEAR any cached
          // selection (don't just bare-return leaving a stale one) — the send path
          // (ChatPanel sendSel) gates only on contextMuted, and the chip is hidden with the
          // tab, so a leftover selection would be sent INVISIBLY. #617 hidden-from-AI bypass.
          if (enforcedAIOverride(ws.aiOverrideMap?.get(tab.fileId)) === 'hidden') {
            editorSelectionRef.current = null
            window.dispatchEvent(new Event('papan:selection-update'))
            return
          }
        }

        editorSelectionRef.current = sel
        window.dispatchEvent(new Event('papan:selection-update'))
      }, 150)
    }

    const onText = () => handleSelectionChange(true)
    const onEditorEvent = () => handleSelectionChange(false)
    document.addEventListener('selectionchange', onText)
    window.addEventListener('papan:editor-selection-change', onEditorEvent)
    return () => {
      document.removeEventListener('selectionchange', onText)
      window.removeEventListener('papan:editor-selection-change', onEditorEvent)
      clearTimeout(selectionTimerRef.current)
    }
  }, [isActive, ws.tabs, ws.aiOverrideMap])

  // Listen for add-sheet dialog requests from SpreadsheetEditor
  useEffect(() => {
    const handler = (e: Event) => {
      const { defaultName, parentId, sourceFileId } = (e as CustomEvent).detail || {}
      if (defaultName) setAddSheetTarget({ defaultName, parentId: parentId ?? null, sourceFileId })
    }
    window.addEventListener('papan:wb-open-add-sheet-dialog', handler)
    return () => window.removeEventListener('papan:wb-open-add-sheet-dialog', handler)
  }, [])

  // Listen for relink-sheet requests from sheet tab context menu
  useEffect(() => {
    const handler = (e: Event) => {
      const { sheetId, sheetName, sourceFileId } = (e as CustomEvent).detail || {}
      if (!sheetId) return
      // Get the parent folder from the active tab's file info
      const activeTab = ws.tabs.find((t: any) => t.fileId === sourceFileId)
      const parentId = activeTab?.parentId ?? null
      setRelinkTarget({ sheetId, sheetName, parentId, sourceFileId })
    }
    window.addEventListener('papan:wb-relink-sheet', handler)
    return () => window.removeEventListener('papan:wb-relink-sheet', handler)
  }, [ws.tabs])

  // Listen for remove-sheet requests — show confirm dialog
  const confirm = useConfirm()
  useEffect(() => {
    const handler = async (e: Event) => {
      const { sheetId, sheetName, isSelf, sheetCount } = (e as CustomEvent).detail || {}
      if (!sheetId) return

      const isLastTwo = sheetCount <= 2
      const message = isSelf
        ? `Unlink "${sheetName}" from this workbook? This tab will reload as a standalone CSV.`
        : isLastTwo
          ? `Unlink "${sheetName}" from this workbook? The workbook will be dissolved and both files will become standalone.`
          : `Unlink "${sheetName}" from this workbook? The CSV file will be kept.`

      const proceed = await confirm({
        title: 'Unlink Sheet',
        message,
        type: 'warning',
        confirmText: 'Unlink',
        cancelText: 'Cancel',
      })

      if (!proceed) return
      const { sourceFileId } = (e as CustomEvent).detail || {}
      window.dispatchEvent(new CustomEvent('papan:wb-remove-sheet', {
        detail: { sheetId, deleteFile: false, sourceFileId },
      }))
    }
    window.addEventListener('papan:wb-remove-sheet-request', handler)
    return () => window.removeEventListener('papan:wb-remove-sheet-request', handler)
  }, [confirm])

  // Listen for workbook group operation responses (errors, merge prompts)
  useEffect(() => {
    const handler = async (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (!detail) return

      if (detail.status === 'error' && detail.error === 'already_in_workbook') {
        toast.error(`"${detail.fileName}" is already in this workbook.`)
        return
      }
      // E7 PR 1: a `.chain` / `.shared` is never a workbook sheet (refused by the server).
      if (detail.status === 'error' && detail.error === 'pointer_not_sheet') {
        toast.error(tCollab('workbook.pointerNotSheet', { fileName: detail.fileName }))
        return
      }
      // #1397: Copy sheet refused — the source's stored copy is damaged (the server's words, nothing was created)
      const damaged = detail.status === 'error' ? damagedMessageFrom(detail) : null
      if (damaged) {
        toast.error(damaged)
        return
      }

      if (detail.status === 'merge_prompt') {
        const otherSheets = (detail.otherMembers as string[]) || []
        const proceed = await confirm({
          title: 'Merge Workbook Groups',
          message: `"${detail.fileName}" belongs to another workbook with ${otherSheets.length} sheet${otherSheets.length === 1 ? '' : 's'} (${otherSheets.join(', ')}). Merge both workbooks into one?`,
          type: 'warning',
          confirmText: 'Merge',
          cancelText: 'Cancel',
        })
        if (!proceed) return
        // Send merge request back through the same add-sheet flow
        const ctx = lastAddSheetContextRef.current
        window.dispatchEvent(new CustomEvent('papan:wb-add-sheet', {
          detail: {
            type: 'merge',
            fileName: detail.fileName,
            parentId: ctx?.parentId,
            mergeWorkbookId: detail.otherWorkbookId,
            sourceFileId: ctx?.sourceFileId,
          },
        }))
      }
    }
    window.addEventListener('papan:wb-response', handler)
    return () => window.removeEventListener('papan:wb-response', handler)
  }, [confirm, toast, tCollab])

  // Mute context chip — hides from AI but doesn't touch editor selection
  const handleMuteContext = useCallback(() => {
    contextMutedRef.current = true
    window.dispatchEvent(new Event('papan:selection-update'))
  }, [])

  // Focus the active editor (restores blurred selection via programmatic focus)
  const focusActiveEditor = useCallback(() => {
    const tabId = activeTabIdRef.current
    if (!tabId) return
    tabEditorRefs.current.get(tabId)?.current?.focusEditor?.()
  }, [])

  /** Read by the save handlers — refs, so their identities don't follow every cover change. */
  const tabCoversRef = useRef(tabCovers)
  tabCoversRef.current = tabCovers
  /**
   * #929/#930 — a tab whose editor is COVERED (its own cover, or any open version preview — `isTabCovered`) is never
   * saved or discarded through here. Every per-tab route goes through these two — File menu, tab menu, `file.*`,
   * Cmd+S — so one missed `disabled` flag can't write a hidden document. The routes still disable themselves (and
   * Cmd+S says why); this is the backstop.
   */
  const tabIsCovered = useCallback((tabId: string) =>
    isTabCovered(tabId, tabCoversRef.current, versionPreviewOpenRef.current), [])
  /** The editor's OWN cover only (the conflict panel) — for the preview's own controls, which pass the preview half. */
  const tabHasOwnCover = useCallback((tabId: string | null | undefined) =>
    !!tabId && isTabCovered(tabId, tabCoversRef.current, false), [])
  const handleSaveTab = useCallback((tabId: string) => {
    if (tabIsCovered(tabId)) return
    tabSaveRefs.current.get(tabId)?.current?.()
  }, [tabIsCovered])

  const handleDiscardTab = useCallback((tabId: string) => {
    if (tabIsCovered(tabId)) return
    tabDiscardRefs.current.get(tabId)?.current?.()
  }, [tabIsCovered])

  const handleSaveAll = useCallback(() => {
    for (const tab of ws.tabs) {
      // #930 — never a tab whose editor covers itself (the conflict "Saved version" panel): that would write its
      // hidden live document. Skipped here, the one place every Save All route (File menu, tab menu, `file.saveAll`)
      // goes through; the rest still save.
      if (tab.isDirty && !tabHasOwnCover(tab.id)) tabSaveRefs.current.get(tab.id)?.current?.()
    }
  }, [ws.tabs, tabHasOwnCover])

  const handleSaveAs = useCallback(async (tabId: string) => {
    if (tabIsCovered(tabId)) return   // copies the hidden live document — see tabIsCovered
    const tab = ws.tabs.find(t => t.id === tabId)
    if (!tab) return
    try {
      const info = await fetchAndCacheFileInfo(queryClient, tab.fileId)
      setSaveAsTarget({ fileId: tab.fileId, name: tab.title, parentId: info.parentId })
    } catch {
      setSaveAsTarget({ fileId: tab.fileId, name: tab.title, parentId: null })
    }
  }, [ws.tabs, queryClient, tabIsCovered])

  const handleDownloadTab = useCallback((tabId: string) => {
    const tab = ws.tabs.find(t => t.id === tabId)
    if (tab) filesApi.download([tab.fileId])
  }, [ws.tabs])

  useEffect(() => {
    if (!isActive) return
    const handler = (e: KeyboardEvent) => {
      // `'s'` only — with Shift held the browser reports `'S'`, so Cmd+Shift+S
      // never reaches this handler and the Save All branch below is dead. That
      // predates this branch (and `file.saveAll`'s registered shortcut does not
      // fire either: the 'workspace' scope has no keyboard region, #900), so it
      // is reported rather than fixed here — but the
      // guard must not claim to cover a keystroke that never arrives.
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        // Not while a version preview covers the editor: the file underneath
        // is still mounted and still dirty, so this saved it invisibly, with
        // the user looking at an old version. `preventDefault` stays outside
        // the guard — the browser's own Save-page dialog over a preview is not
        // what they meant either.
        //
        // Why the notice is conditional on dirtiness lives on
        // `useSaveBlockedNotice`; restating it at each call site is how the
        // two drifted apart in the first place.
        if (versionPreviewOpenRef.current) {
          noticeSaveBlocked(hasDirtyRef.current, closePreview)
          return
        }
        // #930 — the editor covers itself (the conflict "Saved version" panel): refused with ITS notice and its way
        // back. Not a silent overwrite: keeping your changes over the saved file is the banner's explicit choice.
        // (Cmd+Shift+S never reaches here — see the `'s'` note above; Save All skips covered tabs itself.)
        const cover = activeCoverRef.current
        if (cover && !e.shiftKey) {
          noticeSaveBlocked(hasDirtyRef.current, cover.uncover, cover)
          return
        }
        if (e.shiftKey) {
          handleSaveAll()
        } else {
          const id = activeTabIdRef.current
          if (id) handleSaveTab(id)
        }
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isActive, handleSaveAll, handleSaveTab, noticeSaveBlocked, closePreview])

  // Unsaved changes confirmation for close actions — three-button dialog
  // Returns a promise that resolves to 'cancel' | 'leave' | 'discard'
  const [unsavedDialog, setUnsavedDialog] = useState<{
    dirtyNames: string[]
    dirtyTabIds: string[]
    resolve: (action: UnsavedAction) => void
  } | null>(null)

  const confirmDirtyClose = useCallback((dirtyTabs: Array<{ id: string; title: string }>): Promise<UnsavedAction> => {
    if (dirtyTabs.length === 0) return Promise.resolve('leave' as UnsavedAction)
    return new Promise((resolve) => {
      setUnsavedDialog({
        dirtyNames: dirtyTabs.map(t => t.title),
        dirtyTabIds: dirtyTabs.map(t => t.id),
        resolve,
      })
    })
  }, [])

  const handleUnsavedAction = useCallback((action: UnsavedAction) => {
    if (!unsavedDialog) return
    if (action === 'discard') {
      // Call discard on each dirty tab before resolving
      for (const tabId of unsavedDialog.dirtyTabIds) {
        tabDiscardRefs.current.get(tabId)?.current?.()
      }
    }
    unsavedDialog.resolve(action)
    setUnsavedDialog(null)
  }, [unsavedDialog])

  const handleCloseTab = useCallback(async (tabId: string) => {
    const tab = ws.tabs.find(t => t.id === tabId)
    if (tab?.isDirty) {
      const action = await confirmDirtyClose([{ id: tab.id, title: tab.title }])
      if (action === 'cancel') return
    }
    ws.closeTab(tabId)
  }, [ws.tabs, ws.closeTab, confirmDirtyClose])

  const handleCloseOthers = useCallback(async (tabId: string) => {
    const dirtyTabs = ws.tabs.filter(t => t.id !== tabId && t.isDirty)
    if (dirtyTabs.length > 0) {
      const action = await confirmDirtyClose(dirtyTabs.map(t => ({ id: t.id, title: t.title })))
      if (action === 'cancel') return
    }
    ws.closeOtherTabs(tabId)
  }, [ws.tabs, ws.closeOtherTabs, confirmDirtyClose])

  const handleCloseToRight = useCallback(async (tabId: string) => {
    const idx = ws.tabs.findIndex(t => t.id === tabId)
    const dirtyTabs = ws.tabs.slice(idx + 1).filter(t => t.isDirty)
    if (dirtyTabs.length > 0) {
      const action = await confirmDirtyClose(dirtyTabs.map(t => ({ id: t.id, title: t.title })))
      if (action === 'cancel') return
    }
    ws.closeTabsToRight(tabId)
  }, [ws.tabs, ws.closeTabsToRight, confirmDirtyClose])

  const handleCloseAll = useCallback(async () => {
    const dirtyTabs = ws.tabs.filter(t => t.isDirty)
    if (dirtyTabs.length > 0) {
      const action = await confirmDirtyClose(dirtyTabs.map(t => ({ id: t.id, title: t.title })))
      if (action === 'cancel') return
    }
    ws.closeAllTabs()
  }, [ws.tabs, ws.closeAllTabs, confirmDirtyClose])

  const handleCloseWorkspace = useCallback(async () => {
    const dirtyTabs = ws.tabs.filter(t => t.isDirty)
    if (dirtyTabs.length > 0) {
      const action = await confirmDirtyClose(dirtyTabs.map(t => ({ id: t.id, title: t.title })))
      if (action === 'cancel') return
    }

    // Close in workspace manager and navigate to next workspace or empty view
    const remaining = wsManager.openWorkspaces.filter(w => w.folderId !== folderId)
    wsManager.closeWorkspace(folderId)

    if (remaining.length > 0) {
      const next = remaining[0]
      wsManager.setActiveWorkspace(next.folderId)
      navigate(`/t/${tenantId}/workspace/${next.folderId}`)
    } else {
      navigate(`/t/${tenantId}/workspace`)
    }
  }, [ws.tabs, confirmDirtyClose, folderId, wsManager, tenantId, navigate])

  // Track focus context: explorer sidebar vs editor area
  // Use mousedown (capture phase) because canvas-based editors like Univer
  // don't fire focusin events. Also listen to focusin for keyboard navigation.
  // Only active workspace should intercept focus events.
  // Switch edit context based on where the user clicks.
  // Explorer click → explorer context (shows Rename, AI Access in Edit menu).
  // Editor click → editor context (shows document-level operations).
  // Only mousedown is used (not focusin) to avoid switching context when
  // opening a file from explorer causes the editor to receive focus.
  useEffect(() => {
    if (!isActive) return
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (explorerContainerRef.current?.contains(target)) {
        setEditContext('explorer')
      } else if (editorContainerRef.current?.contains(target)) {
        setEditContext('editor')
      }
    }
    document.addEventListener('mousedown', onMouseDown, true)
    return () => {
      document.removeEventListener('mousedown', onMouseDown, true)
    }
  }, [isActive])

  // Edit actions from explorer ref (file-level operations)
  const explorerEditActions = useMemo(() => ({
    copy: () => explorerRef.current?.copy(),
    cut: () => explorerRef.current?.cut(),
    paste: () => explorerRef.current?.paste(),
    delete: () => explorerRef.current?.delete(),
    getSelectedName: () => explorerRef.current?.getSelectedName() ?? null,
    getSelectedCount: () => explorerRef.current?.getSelectedCount() ?? 0,
    getSelectedNames: () => explorerRef.current?.getSelectedNames() ?? [],
    getSelectedIds: () => explorerRef.current?.getSelectedIds() ?? [],
    getPasteTargetName: () => explorerRef.current?.getPasteTargetName() ?? null,
    getDeleteLabel: () => explorerRef.current?.getDeleteLabel() ?? 'Delete',
    rename: () => explorerRef.current?.rename(),
  }), [])

  // Edit actions from active tab's editor ref (document-level operations)
  const editorEditActions = useMemo(() => ({
    copy: () => { const id = activeTabIdRef.current; if (id) tabEditorRefs.current.get(id)?.current?.copy() },
    cut: () => { const id = activeTabIdRef.current; if (id) tabEditorRefs.current.get(id)?.current?.cut() },
    paste: () => { const id = activeTabIdRef.current; if (id) tabEditorRefs.current.get(id)?.current?.paste() },
    delete: () => { const id = activeTabIdRef.current; if (id) tabEditorRefs.current.get(id)?.current?.delete() },
    getSelectedName: () => null as string | null,
  }), [])

  // File actions from explorer (gated by canWrite)
  const fileActions = useMemo(() => ({
    createFolder: canWrite ? () => explorerRef.current?.createFolder() : undefined,
    newFile: canWrite ? () => explorerRef.current?.newFileQuick() : undefined,
    prepareUpload: canWrite ? () => explorerRef.current?.prepareUpload() : undefined,
    linkFolder: canWrite ? () => explorerRef.current?.linkFolder() : undefined,
    openFilePicker: () => setShowFilePicker(true),
  }), [canWrite])

  // Workspace list for "Open Project" and the Open Recent submenu
  const [recentWorkspaces, setRecentWorkspaces] = useState<RecentWorkspace[]>([])
  const [showWorkspacePicker, setShowWorkspacePicker] = useState(false)

  // Load recent workspaces on mount (per-email for orgs)
  useEffect(() => {
    if (!isActive) return
    workspaceApi.listRecent().then(items => {
      setRecentWorkspaces(items.map(w => ({ folderId: w.folderId, name: w.name, path: w.path })))
    }).catch(() => {})
  }, [isActive])

  const newProject = useNewProject()
  // Both menu items funnel through the shared New-Project dialog, which handles
  // the standalone/for-a-project + audience choices per entity kind and returns
  // the created folder id.
  const handleNewEntity = useCallback((entityKind: 'project' | 'miniapp') => {
    newProject.open({ entityKind, onDone: (fid) => navigate(`/t/${tenantId}/workspace/${fid}`) })
  }, [newProject, tenantId, navigate])

  // Reveal this workspace's folder in the Drive (highlight it among its
  // siblings). Mirrors the WorkspaceExplorer "Reveal in Drive" URL convention.
  const handleRevealInDrive = useCallback(() => {
    const parentId = ws.workspaceParentId
    const base = parentId ? `/t/${tenantId}/folder/${parentId}` : `/t/${tenantId}/folder`
    navigate(ws.folderId ? `${base}?reveal=${ws.folderId}` : base)
  }, [tenantId, navigate, ws.workspaceParentId, ws.folderId])

  const handleOpenRecentWorkspace = useCallback((wsFolderId: string) => {
    navigate(`/t/${tenantId}/workspace/${wsFolderId}`)
  }, [tenantId, navigate])

  // Focus context, and NOT the editor's while a version preview covers it.
  //
  // Why it would otherwise BE the editor's: these actions act on the live editor
  // through `tabEditorRefs`, and the preview renders INSIDE
  // `editorContainerRef` — so clicking it to scroll runs the capture-phase
  // mousedown listener above and sets the context to 'editor'. Edit → Delete then cut the hidden
  // file under an overlay that does not change.
  //
  // Gating `editContext` rather than just the actions: the menu reads it for
  // labels and enablement too, so swapping only the actions would offer
  // editor-looking items that run the explorer's file operations.
  //
  // Rarely reached: the registry supplies the Edit menu's items whenever a tab
  // is open, so `editActions` is the migration-era fallback (WorkspaceMenuBar,
  // `editCommands.length > 0 ? … : …`). An editor that registers those ids
  // ENABLED for itself (the database grid's Delete Rows) is #929, answered by
  // the registry's cover. Gated on `editorCovered` — any cover, the conflict
  // panel included — so the fallback can't act on a hidden editor either.
  const previewEditContext = editorCovered ? 'explorer' : editContext
  const editActions = previewEditContext === 'editor' && ws.activeTab
    ? editorEditActions
    : explorerEditActions

  // Browser beforeunload warning when any tab is dirty — never during an account-end load (#1316, `beforeUnloadPrompt`)
  useEffect(() => {
    const hasDirty = ws.tabs.some(t => t.isDirty)
    if (!hasDirty) return
    const handler = (e: BeforeUnloadEvent) => beforeUnloadPrompt(e)
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [ws.tabs])

  // Clamp panel widths on mount / window resize so they never overflow
  const panelWidthsRef = useRef({ explorer: ws.explorerWidth, chat: ws.chatWidth, ec: explorerCollapsed, cc: chatCollapsed })
  panelWidthsRef.current = { explorer: ws.explorerWidth, chat: ws.chatWidth, ec: explorerCollapsed, cc: chatCollapsed }

  useEffect(() => {
    const clamp = () => {
      const container = panelsContainerRef.current
      if (!container) return
      const { explorer, chat, ec, cc } = panelWidthsRef.current
      // A collapsed panel occupies 0 — count it as such, but still clamp the
      // panel that IS visible. (The old guard bailed when EITHER panel was
      // collapsed, so a collapsed explorer + wide chat + a window shrink left
      // the chat panel clipped off-screen.) A collapsed panel's stored width
      // is never touched, so it restores to the user's size.
      const effExplorer = ec ? 0 : explorer
      const effChat = cc ? 0 : chat
      if (effExplorer + effChat === 0) return
      const total = container.clientWidth
      const combined = effExplorer + effChat + MIN_EDITOR + CHROME_WIDTH
      if (combined <= total) return
      const excess = combined - total
      if (!ec && !cc) {
        // Both visible — shrink proportionally, respecting minimums.
        const explorerShrink = Math.min(explorer - MIN_EXPLORER, excess * (explorer / (explorer + chat)))
        const chatShrink = excess - explorerShrink
        ws.setExplorerWidth(Math.max(MIN_EXPLORER, explorer - explorerShrink))
        ws.setChatWidth(Math.max(MIN_CHAT, chat - chatShrink))
      } else if (!ec) {
        ws.setExplorerWidth(Math.max(MIN_EXPLORER, explorer - excess))
      } else if (!cc) {
        ws.setChatWidth(Math.max(MIN_CHAT, chat - excess))
      }
    }
    clamp()
    window.addEventListener('resize', clamp)
    return () => window.removeEventListener('resize', clamp)
  }, [ws.configLoaded, explorerCollapsed, chatCollapsed]) // eslint-disable-line react-hooks/exhaustive-deps

  // Block iframes from capturing mouse events during panel resize drags,
  // and force the resize cursor page-wide so it holds while dragging over
  // the editor (not just when hovering the thin handle).
  const [resizing, setResizing] = useState<'explorer' | 'chat' | null>(null)
  const blockIframes = () => {
    document.body.style.setProperty('--iframe-pointer', 'none')
    document.body.style.cursor = 'col-resize'
  }
  const unblockIframes = () => {
    document.body.style.removeProperty('--iframe-pointer')
    document.body.style.cursor = ''
  }

  // Panel resize writes the width straight to the DOM during the drag and
  // only commits to React state on mouseup. Calling setXWidth per mousemove
  // re-rendered the ENTIRE workspace — including every mounted editor tab
  // (13–15 of them once you've been working a while) — 60×/sec, which is
  // what made dragging lag. The editor flexes natively as the sibling
  // width changes; no React in the hot path.
  //
  // One handler drives both side panels in both directions:
  //   • collapsing (drag past the min → snap closed), with LIVE pull-back —
  //     the drag stays alive after it snaps shut, so easing back past
  //     RESTORE_OVER reopens it without ever releasing the pointer;
  //   • expanding a collapsed panel by grabbing the thin handle at the page
  //     edge (`fromCollapsed`) and pulling it back out (starts at width 0).
  // The width is written straight to the DOM during the drag; React state is
  // only committed on pointer-up (see the perf note above).
  const beginResize = (side: 'explorer' | 'chat', e: React.PointerEvent, fromCollapsed: boolean) => {
    e.preventDefault()
    blockIframes()
    setResizing(side)
    const isExplorer = side === 'explorer'
    const el = (isExplorer ? explorerContainerRef : chatContainerRef).current
    const editorEl = editorContainerRef.current
    const MIN = isExplorer ? MIN_EXPLORER : MIN_CHAT
    const setWidth = isExplorer ? ws.setExplorerWidth : ws.setChatWidth
    const setCollapsed = isExplorer ? setExplorerCollapsed : setChatCollapsed
    // Only the explorer's `.ws-card` carries a 1px border — zero it while the
    // panel is visually collapsed so it doesn't render as a sliver at width 0.
    const hasBorder = isExplorer
    // Drive the panel's margins during the drag so its 12px inset appears the
    // instant it reopens (not stuck to the edge until pointer-up commits React
    // state), and the −2 trim tucks it flush while collapsed. Outer margin =
    // the inset side; inner margin = the trim side.
    const setMargins = (isCollapsed: boolean) => {
      if (!el) return
      const inset = isCollapsed ? '0px' : `${PANEL_INSET}px`
      const trim = isCollapsed ? `${COLLAPSE_TRIM}px` : ''
      if (isExplorer) { el.style.marginLeft = inset; el.style.marginRight = trim }
      else { el.style.marginRight = inset; el.style.marginLeft = trim }
    }
    if (el) {
      el.style.transition = 'none'   // direct, lag-free while dragging
      if (hasBorder) el.style.borderWidth = fromCollapsed ? '0' : ''
    }
    const startX = e.clientX
    // Explorer grows rightward (+dx), chat grows leftward (−dx).
    const sign = isExplorer ? 1 : -1
    const startWidth = fromCollapsed ? 0 : (isExplorer ? ws.explorerWidth : ws.chatWidth)
    const containerWidth = panelsContainerRef.current?.clientWidth ?? Infinity
    const effOther = isExplorer
      ? (chatCollapsed ? 0 : ws.chatWidth)
      : (explorerCollapsed ? 0 : ws.explorerWidth)
    const maxWidth = containerWidth - effOther - MIN_EDITOR - CHROME_WIDTH
    let latest = startWidth
    let collapsed = fromCollapsed

    const teardown = () => {
      unblockIframes()
      setResizing(null)
      document.removeEventListener('pointermove', onPointerMove)
      document.removeEventListener('pointerup', onPointerUp)
      document.removeEventListener('pointercancel', onPointerUp)
      if (el) el.style.transition = ''     // re-enable the CSS width transition
      if (editorEl) { editorEl.style.marginLeft = ''; editorEl.style.marginRight = '' }  // clear any budge
    }
    const onPointerMove = (ev: PointerEvent) => {
      const desired = startWidth + sign * (ev.clientX - startX)
      let budge = 0
      if (fromCollapsed) {
        // Unhide: the panel stays hidden while you drag; the editor budges
        // toward its landing spot as a pre-snap tell, then the panel SNAPS in
        // once you cross UNHIDE_SNAP (re-hides below UNHIDE_HIDE — hysteresis).
        const d = Math.max(0, desired)           // px dragged out from the edge
        if (!collapsed && d < UNHIDE_HIDE) collapsed = true
        else if (collapsed && d >= UNHIDE_SNAP) collapsed = false
        if (collapsed) {
          latest = 0
          // Quartic: stays put through the first half of the pull, then ramps
          // hard near the threshold (slope > 1:1) so it reads as a magnet grab.
          budge = UNHIDE_BUDGE * Math.min(1, d / UNHIDE_SNAP) ** 4
        } else {
          latest = Math.min(maxWidth, Math.max(MIN, d))              // snapped in, then tracks past its min
        }
      } else if (desired < MIN) {
        const over = MIN - desired               // px below the min
        // Hysteresis: snap closed past SNAP_OVER, reopen once back inside
        // RESTORE_OVER. Between the two thresholds the state is sticky — governs
        // what we COMMIT on release (hidden vs open).
        if (!collapsed && over >= SNAP_OVER) collapsed = true
        else if (collapsed && over <= RESTORE_OVER) collapsed = false
        latest = collapsed ? 0 : MIN - STRETCH * (1 - Math.exp(-over / STRETCH))  // rubber-band below the min
      } else {
        collapsed = false
        latest = Math.min(maxWidth, desired)   // clamp so the editor keeps its min
      }
      if (el) {
        el.style.width = `${latest}px`
        // Key the collapsed *look* (no border, tucked margins) off the actual
        // width, so a partly-pulled-open panel already shows its inset + border.
        const lookCollapsed = latest <= 0
        if (hasBorder) el.style.borderWidth = lookCollapsed ? '0' : ''
        setMargins(lookCollapsed)
      }
      if (editorEl) {
        // Budge the editor toward the panel's side (explorer → right margin
        // grows on its left; chat → on its right); cleared once it snaps in.
        editorEl.style.marginLeft = (isExplorer && budge) ? `${budge}px` : ''
        editorEl.style.marginRight = (!isExplorer && budge) ? `${budge}px` : ''
      }
    }
    const onPointerUp = () => {
      teardown()
      if (collapsed) {
        setCollapsed(true)                 // commit collapse (React holds width 0)
        return
      }
      const finalW = Math.max(MIN, Math.min(maxWidth, latest))  // settle inside [min, max]
      if (el) el.style.width = `${finalW}px`  // CSS transition animates the snap-back
      setWidth(finalW)
      setCollapsed(false)                  // reopen if we expanded from the edge
    }

    // pointer* (not mouse*) so touch + pen drags work; pointercancel covers
    // the browser reclaiming the gesture mid-drag.
    document.addEventListener('pointermove', onPointerMove)
    document.addEventListener('pointerup', onPointerUp)
    document.addEventListener('pointercancel', onPointerUp)
  }

  // Restore a collapsed panel to its default width (animated via the CSS
  // width transition on .ws-panel-collapsible).
  const restoreExplorer = () => { ws.setExplorerWidth(DEFAULT_EXPLORER); setExplorerCollapsed(false) }
  const restoreChat = () => { ws.setChatWidth(DEFAULT_CHAT); setChatCollapsed(false) }

  // Double-click the tab bar toggles BOTH side panels via the same collapse
  // mechanism (so the restore tabs show): if either is visible, collapse
  // both; if both are already collapsed, bring both back.
  const toggleBothPanels = () => {
    const hide = !(explorerCollapsed && chatCollapsed)
    setExplorerCollapsed(hide)
    setChatCollapsed(hide)
  }

  // ─── Editor area drag-and-drop (upload external files OR open Papan files) ──
  const handleEditorDragEnter = useCallback((e: React.DragEvent) => {
    const isPapan = isPapanDrag(e.dataTransfer)
    const isExternal = e.dataTransfer.types.includes('Files')
    if (!isExternal && !isPapan) return
    e.preventDefault()
    // Internal file/tab drags (explorer rows AND editor tabs both carry
    // PAPAN_DRAG_MIME) open silently, browser-tab style — no full-pane dropzone
    // (issue #229). The drop still fires (dragOver/drop below handle PAPAN);
    // only the overlay is suppressed. The overlay is reserved for EXTERNAL
    // OS-file drags, where "Drop to upload and open" signals a new action.
    if (isExternal && !isPapan) {
      editorDragCounter.current++
      setEditorDropActive(true)
    }
  }, [])

  const handleEditorDragOver = useCallback((e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('Files') && !isPapanDrag(e.dataTransfer)) return
    e.preventDefault()
    e.dataTransfer.dropEffect = isPapanDrag(e.dataTransfer) ? 'move' : 'copy'
  }, [])

  const handleEditorDragLeave = useCallback((e: React.DragEvent) => {
    // Only external-file drags drive the overlay counter (see dragEnter) —
    // internal PAPAN drags never incremented it, so a leave during one must
    // not decrement into negatives.
    const isExternal = e.dataTransfer.types.includes('Files') && !isPapanDrag(e.dataTransfer)
    if (!isExternal) return
    editorDragCounter.current--
    if (editorDragCounter.current <= 0) {
      editorDragCounter.current = 0
      setEditorDropActive(false)
    }
  }, [])

  const handleEditorDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    editorDragCounter.current = 0
    setEditorDropActive(false)

    // Internal Papan file drop → open files + link parent folder if not covered
    if (isPapanDrag(e.dataTransfer)) {
      const transfer = readPapanDrag(e.dataTransfer)
      if (!transfer) return
      try {
        const items = transfer.files
        const files = items.filter(f => !f.isFolder)
        const folders = items.filter(f => f.isFolder)

        // Open files in tabs immediately
        for (const file of files) {
          await ws.openFile(file.id)
        }
        // Deferred activation — ensures tab content renders even if
        // setActiveTabId from openFile hasn't been processed yet
        if (files.length > 0) {
          setPendingActiveFileId(files[files.length - 1].id)
        }

        // Link uncovered parent folders (check ancestry up to 10 levels)
        const coveredIds = new Set([
          ...(ws.folderId ? [ws.folderId] : []),
          ...ws.links.map(l => l.targetFolderId),
        ])
        const linked = new Set<string>()

        for (const file of files) {
          try {
            const info = await fetchAndCacheFileInfo(queryClient, file.id)
            if (!info.parentId || linked.has(info.parentId)) continue
            if (await isAncestorCovered(queryClient, info.parentId, coveredIds)) continue
            linked.add(info.parentId)
            ws.addLink(info.parentId)
          } catch { /* skip */ }
        }

        for (const folder of folders) {
          if (linked.has(folder.id)) continue
          if (await isAncestorCovered(queryClient, folder.id, coveredIds)) continue
          linked.add(folder.id)
          ws.addLink(folder.id)
        }
      } catch {
        // Invalid data
      }
      return
    }

    // External file drop → upload to workspace files + open.
    // Capture the dropped entries synchronously: collectDroppedFiles() runs its
    // webkitGetAsEntry() pass before its first await, so the entries (including
    // directories, for recursion) survive the DataTransfer being detached across
    // `await ensureWorkspace()` below. Reading after the await would lose dropped
    // folders (see issue #49). The error handler is attached here, not after the
    // ensureWorkspace await, so a rejected read can never sit unhandled; on
    // failure it surfaces a toast and resolves to null.
    const collected = collectDroppedFiles(e).catch((err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to process drop')
      return null
    })
    // #1316 review round 2 (owner, 2026-10-28): the upload the person started FINISHES as the identity that started it
    // (folders, conflict checks, every file); what it SHOWS — toasts, the files opened in tabs — stops with its session
    const batch = pinnedBatch()

    let targetId = ws.folderId
    if (!targetId) {
      targetId = await ws.ensureWorkspace()
      if (!targetId) return
    }

    const snapshot = await collected
    if (!snapshot) return   // collect failed (toast already shown)

    let result: Awaited<ReturnType<typeof processCollectedDrop>>
    try {
      result = await processCollectedDrop(snapshot, {
        targetFolderId: targetId,
        resolveConflict,
        batch,
      })
    } catch (err) {
      if (!batch.ended()) toast.error(err instanceof Error ? err.message : 'Failed to process drop')
      return
    }
    if (!result || result.uploads.length === 0) return

    const uploadedFileIds: string[] = []
    let replacingInBackground = 0
    const sent = await uploadFilesAsBatch(batch, result.uploads, ({ file, parentId, conflictStrategy }, opts) =>
      filesApi.uploadFile(parentId, file.name, file, { conflictStrategy, ...opts }))
    for (const res of sent.done) {
      // #1090 — a large app database is replaced in the background (202): not opened as if it had changed already.
      if (res.queued) { replacingInBackground++; continue }
      if (!res.skipped) uploadedFileIds.push(res.fileId)
    }
    // a file whose identity was lost with its session is not sent — said so, as the upload dock does
    if (sent.lost > 0) toast.error(tFiles('driveView.toast.uploadIdentityLost'))

    if (batch.ended()) return
    if (replacingInBackground > 0) toast.success('Uploaded — the database is being replaced in the background; you will be notified when it is done')
    if (uploadedFileIds.length > 0) {
      toast.success(`Uploaded ${uploadedFileIds.length} file${uploadedFileIds.length > 1 ? 's' : ''}`)
      // Open each uploaded file in a tab
      for (const fileId of uploadedFileIds) {
        await ws.openFile(fileId)
      }
      // Activate the last uploaded file via effect (deferred until tab is in state)
      setPendingActiveFileId(uploadedFileIds[uploadedFileIds.length - 1])
    }
  }, [ws.folderId, ws.ensureWorkspace, ws.openFile, resolveConflict, toast, queryClient, tFiles])

  // Show loading while loading config
  if (!ws.configLoaded) {
    return (
      <div className="flex-1 flex items-center justify-center themed-text-muted">
        <div className="text-center">
          <Loader2 size={32} className="mx-auto mb-3 animate-spin opacity-50" />
          <p className="text-sm">Loading workspace...</p>
        </div>
      </div>
    )
  }

  // Show error if workspace config failed
  if (ws.configError) {
    return (
      <div className="flex-1 flex items-center justify-center themed-text-muted">
        <div className="text-center">
          <AlertCircle size={32} className="mx-auto mb-3 text-red-400 opacity-70" />
          <p className="text-sm text-red-400">{ws.configError}</p>
        </div>
      </div>
    )
  }

  return (
    // #900 — shortcuts from the keyboard only while this workspace is the one on screen (WorkspaceCache keeps the
    // others mounted behind display:none, each with its own registry).
    <CommandRegistryProvider keyboard={isActive}>
    <WorkspaceCommandRegistration
      explorerContainerRef={explorerContainerRef}
      editorContainerRef={editorContainerRef}
      ws={ws}
      handleSaveTab={handleSaveTab}
      handleSaveAll={handleSaveAll}
      handleDiscardTab={handleDiscardTab}
      editorCovered={editorCovered}
      saveAllAvailable={saveAllAvailable}
    />
    {/* Transparent — the app root paints the shared ws-stage behind this.
        The menu bar portals into the shell's ONE permanent top bar (App.tsx)
        when this workspace is active; ws-row's 12px sides keep the panels'
        left/right inset aligned with the drive cards. */}
    <div className="flex-1 flex flex-col overflow-hidden min-w-0">
      <ShellTopBarPortal active={isActive}>
      <WorkspaceMenuBar
        workspaceName={ws.workspaceName}
        workspacePath={ws.workspacePath}
        onRename={ws.renameWorkspace}
        onClose={handleCloseWorkspace}
        editActions={editActions}
        fileActions={fileActions}
        editContext={previewEditContext}
        recentFiles={ws.recentFiles}
        onOpenRecentFile={ws.openFile}
        entityKind={ws.entityKind}
        onNewProject={() => handleNewEntity('project')}
        onNewMiniapp={() => handleNewEntity('miniapp')}
        onRevealInDrive={handleRevealInDrive}
        onOpenWorkspace={() => setShowWorkspacePicker(true)}
        recentWorkspaces={recentWorkspaces}
        onOpenRecentWorkspace={handleOpenRecentWorkspace}
        currentWorkspaceFolderId={folderId}
        onSave={() => { const id = ws.activeTabId; if (id) handleSaveTab(id) }}
        onSaveAll={handleSaveAll}
        onSaveAs={() => { const id = ws.activeTabId; if (id) handleSaveAs(id) }}
        onDiscard={() => { const id = ws.activeTabId; if (id) handleDiscardTab(id) }}
        onDownload={() => { const id = ws.activeTabId; if (id) handleDownloadTab(id) }}
        // Not while anything covers the editor (`editorCovered`) — the same reason
        // Cmd+S is guarded. These props are what the File menu actually reads; the
        // `enabled` flags on the `file.*` commands reach nothing, because
        // `getMenuCommands` is only ever asked for 'edit'.
        canSave={!editorCovered && !!ws.activeTabId && !!ws.tabs.find(t => t.id === ws.activeTabId)?.isDirty}
        canSaveAll={saveAllAvailable}
        // Not while a preview covers the editor either. Save As copies the
        // CURRENT document, so offered while the user is reading a three-day-old
        // version it hands them a copy of something else — quietly, under a
        // dialog that never says which version it took. "I want this old one"
        // already has its own control: Restore, in the preview's own bar —
        // except for a PDApp app-version preview, which passes `hideActions`,
        // so Restore lives on the App history panel's bar instead.
        canSaveAs={!editorCovered && !!ws.activeTabId}
        canDownload={!!ws.activeTabId}
        canDiscard={!editorCovered && !!ws.activeTabId && !!ws.tabs.find(t => t.id === ws.activeTabId)?.isDirty}
        onSetAIAccess={(ids, permission) => { for (const id of ids) ws.setAIAccess(id, permission) }}
        onClearAIAccess={(ids) => { for (const id of ids) ws.clearAIAccess(id) }}
        getSelectedAIAccess={() => {
          const ids = explorerRef.current?.getSelectedIds() ?? []
          if (ids.length !== 1) return undefined
          return ws.aiOverrideMap?.get(ids[0])
        }}
        getSelectedAllFolders={() => {
          const items = explorerRef.current?.getSelectedItems?.() ?? []
          return items.length > 0 && items.every(item => item.isFolder)
        }}
      />
      </ShellTopBarPortal>
      <div ref={panelsContainerRef} className="flex-1 flex overflow-hidden min-w-0 ws-row">
      {/* Explorer sidebar — always mounted; collapse controls its width */}
      <>
          <div
            ref={explorerContainerRef}
            // No minWidth: the panel is shrink-0 with an explicit width
            // (always ≥ min after commit), and omitting it lets the drag
            // stretch elastically below the min. borderWidth 0 when collapsed
            // — else the ws-card's 1px border renders as a 2px sliver at
            // width 0 (border-box). The chat wrapper has no border of its own.
            // marginLeft carries the row's left inset (12px) so it collapses
            // to 0 with the panel, letting the editor slide out to the edge.
            // When collapsed, the handle + its two 4px gaps already supply 14px
            // of edge spacing; marginRight −2 trims that to the 12px shell inset
            // so the editor's left edge lands exactly on the drive-shell line.
            style={{ width: explorerCollapsed ? 0 : ws.explorerWidth, marginLeft: explorerCollapsed ? 0 : PANEL_INSET, marginRight: explorerCollapsed ? COLLAPSE_TRIM : undefined, borderWidth: explorerCollapsed ? 0 : undefined }}
            className="ws-card ws-panel-collapsible flex flex-col shrink-0 min-w-0"
          >
            <div className="h-10 border-b themed-border flex items-center px-3 text-xs themed-text-muted uppercase tracking-wide">
              <span className="flex-1">{ws.entityKind === 'miniapp' ? 'PDApp' : 'Project'}</span>
              {ws.entityKind === 'miniapp' && ws.folderId && (
                <div className="flex items-center gap-0.5">
                  <button
                    onClick={() => {
                      if (!ws.folderId) return
                      const prefix = tenantId.startsWith('p_') ? '/~' : `/t/${tenantId}`
                      // "Run as app" opens the full-window app surface, which must
                      // point at the underlying `.miniapp` folder — never a
                      // workspace-shell id. An individual-scope shell id encodes
                      // its target as `wsi_<emailHash>_<miniappFolderId>`; strip the
                      // prefix to recover it. When folderId isn't a shell (the app
                      // was opened directly, so it's already the `.miniapp` id), use
                      // it as-is.
                      const appFolderId = ws.folderId.startsWith('wsi_')
                        ? ws.folderId.replace(/^wsi_[^_]+_/, '')
                        : ws.folderId
                      navigate(`${prefix}/app/${appFolderId}`)
                    }}
                    className="p-1 themed-text-muted hover:themed-text hover:themed-bg-tertiary rounded transition-colors cursor-pointer"
                    data-tooltip="Run as app"
                    aria-label="Run as app"
                  >
                    <AppWindowIcon size={14} />
                  </button>
                  <button
                    onClick={() => { if (ws.folderId) { requestMiniappView(ws.folderId, 'run'); ws.openFile(ws.folderId, { name: ws.workspaceName || undefined }) } }}
                    className="p-1 themed-text-muted hover:themed-text hover:themed-bg-tertiary rounded transition-colors cursor-pointer"
                    data-tooltip="Preview app"
                    aria-label="Preview app"
                  >
                    <Play size={14} />
                  </button>
                  <button
                    onClick={() => { if (ws.folderId) { requestMiniappView(ws.folderId, 'settings'); ws.openFile(ws.folderId, { name: ws.workspaceName || undefined }) } }}
                    className="p-1 themed-text-muted hover:themed-text hover:themed-bg-tertiary rounded transition-colors cursor-pointer"
                    data-tooltip="App settings"
                    aria-label="App settings"
                  >
                    <Settings size={14} />
                  </button>
                </div>
              )}
            </div>
            <WorkspaceExplorer
              ref={explorerRef}
              tenantId={tenantId}
              tenantName={tenantName}
              entityKind={ws.entityKind}
              folderId={ws.folderId}
              links={ws.links}
              canWrite={canWrite}
              canCopy={canCopy}
              userPermMap={userPermMap}
              accessRefreshKey={accessRefreshKey}
              activeFileId={ws.activeTab?.fileId ?? null}
              externalDropHighlight={editorDropActive}
              aiOverrideMap={ws.aiOverrideMap}
              onFileOpen={(fileId, name) => ws.openFile(fileId, name ? { name } : undefined)}
              onFileDeleted={(fileId, { isFolder, path }) => {
                if (!isFolder) {
                  const tab = ws.tabs.find(t => t.fileId === fileId)
                  if (tab) ws.closeTab(tab.id)
                  return
                }
                // Folder deleted — close all tabs whose path is inside it
                if (path) {
                  const prefix = path.endsWith('/') ? path : path + '/'
                  for (const tab of ws.tabs) {
                    if (tab.path.startsWith(prefix)) {
                      ws.closeTab(tab.id)
                    }
                  }
                }
              }}
              onAddLink={ws.addLink}
              onRemoveLink={ws.removeLink}
              onEnsureWorkspace={ws.ensureWorkspace}
              onSetAIAccess={ws.setAIAccess}
              onClearAIAccess={ws.clearAIAccess}
              onMissingLinkClick={() => {
                originalMissingLinksRef.current = [...ws.missingLinks]
                setShowRelinkDialog(true)
              }}
            />
          </div>
          {/* Always rendered: a normal divider when open, and a thin grab
              strip at the freed edge (explorer width + margin both 0) when
              collapsed, so it can be pulled back out. */}
          <ResizeHandle
            onPointerDown={(e) => beginResize('explorer', e, explorerCollapsed)}
            dragging={resizing === 'explorer'}
          />
      </>

      {/* Editor area */}
      <div
        ref={editorContainerRef}
        className="ws-card-hero flex-1 flex flex-col overflow-hidden relative"
        style={{ minWidth: MIN_EDITOR, zIndex: tabOverviewOpen ? 30 : undefined }}
        onDragEnter={handleEditorDragEnter}
        onDragOver={handleEditorDragOver}
        onDragLeave={handleEditorDragLeave}
        onDrop={handleEditorDrop}
      >
        {/* Tab bar — flanked by restore tabs when a side panel is collapsed */}
        <div className="flex items-stretch shrink-0">
        {explorerCollapsed && (
          <button className="ws-panel-restore" onClick={restoreExplorer} title="Show explorer" aria-label="Show explorer">
            <PanelLeftOpen size={16} />
          </button>
        )}
        <div className="flex-1 min-w-0">
        <TabBar
          tabs={ws.tabs}
          activeTabId={ws.activeTabId}
          onSelect={(tabId: string) => {
            if (tabId === ws.activeTabId) {
              focusActiveEditor()
              // Unmute context chip if it was dismissed
              if (contextMutedRef.current) {
                contextMutedRef.current = false
                window.dispatchEvent(new Event('papan:selection-update'))
              }
            }
            ws.setActiveTabId(tabId); setEditContext('editor')
          }}
          onClose={handleCloseTab}
          onCloseOthers={handleCloseOthers}
          onCloseToRight={handleCloseToRight}
          onCloseAll={handleCloseAll}
          onReorder={ws.reorderTabs}
          onDoubleClick={toggleBothPanels}
          onNewFile={fileActions.newFile}
          onOpenFilePicker={fileActions.openFilePicker}
          recentFiles={ws.recentFiles}
          onOpenRecentFile={ws.openFile}
          onSave={handleSaveTab}
          onSaveAll={handleSaveAll}
          onSaveAs={handleSaveAs}
          onDiscard={handleDiscardTab}
          onDownload={handleDownloadTab}
          onClickEmpty={focusActiveEditor}
          tenantId={tenantId}
          onOverviewChange={setTabOverviewOpen}
          versionPreviewOpen={versionPreviewOpen}
          coveredTabIds={coveredTabIds}
          saveAllAvailable={saveAllAvailable}
        />
        </div>
        {chatCollapsed && (
          <button className="ws-panel-restore" onClick={restoreChat} title="Show assistant" aria-label="Show assistant">
            <PanelRightOpen size={16} />
          </button>
        )}
        </div>

        {/* Breadcrumb — full path with chevron separators; click empty space to refocus editor */}
        {ws.activeTab && (
          <div className="ws-breadcrumb h-7 px-3 flex items-center text-xs themed-text-muted border-b themed-border shrink-0 gap-1 overflow-hidden" data-testid="editor-breadcrumb" onClick={focusActiveEditor}>
            <BreadcrumbPath path={ws.activeTab.path} />
            {ws.activeTab.isDirty && (
              <span className={`ml-2 text-[10px] shrink-0 ${ws.activeTab.isConflicted ? 'text-orange-400' : 'themed-text-muted'}`}>
                {ws.activeTab.isConflicted ? '(unsaved · conflict)' : '(unsaved)'}
              </span>
            )}
            <div className="flex-1" />
            {/* Per-file "History" — the file's OWN version history. Offered on
                file tabs only; the app tab shows "App history" instead (#612
                UX fix). */}
            {!isAppTab && (
              <button
                onClick={() => {
                  closePreview()
                  setShowAppVersionPanel(false)
                  setVersionPanelFileId(prev =>
                    prev === ws.activeTab?.fileId ? null : ws.activeTab?.fileId ?? null
                  )
                }}
                className={`flex items-center gap-1 px-1.5 py-0.5 text-xs rounded transition-colors shrink-0 ${
                  versionPanelFileId === ws.activeTab.fileId
                    ? 'themed-bg-tertiary themed-text'
                    : 'themed-text-muted hover:themed-bg-tertiary hover:themed-text'
                }`}
                title="Version history"
              >
                <Clock size={12} />
                <span>History</span>
              </button>
            )}
            {/* On a PDApp's app tab, the app-level history is labelled "History"
                — same as a file's — since to the user it is simply the app's
                history (the PDApp name is the panel's subtitle). Offered ONLY on
                the app tab, mutually exclusive with the per-file panel. */}
            {isAppTab && (
              <button
                onClick={() => {
                  closePreview()
                  setVersionPanelFileId(null)
                  setShowAppVersionPanel(v => !v)
                }}
                className={`flex items-center gap-1 px-1.5 py-0.5 text-xs rounded transition-colors shrink-0 ${
                  showAppVersionPanel
                    ? 'themed-bg-tertiary themed-text'
                    : 'themed-text-muted hover:themed-bg-tertiary hover:themed-text'
                }`}
                title="Version history"
                data-testid="app-version-history-toggle"
              >
                <Clock size={12} />
                <span>History</span>
              </button>
            )}
          </div>
        )}

        {/* Content — all tabs stay mounted, hidden via display:none. (An
            opacity/absolute keep-alive would preserve the preview iframe's
            scroll across tab switches, but it keeps EVERY hidden editor
            iframe — incl. heavy Collabora office frames — painting, which
            lags the workspace. Perf wins over scroll retention here.) */}
        <div className="flex-1 overflow-hidden min-h-0 flex flex-row relative">
          {/* Editor content. Marked with its workspace: a client-executed
              agent tool is bound to a workspace, and every open workspace
              stays mounted, so "the focused editor" alone is ambiguous (#352). */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative" {...{ [WORKSPACE_SCOPE_ATTR]: ws.workspaceId ?? undefined, [WORKSPACE_LOADED_ATTR]: ws.configLoaded ? '1' : undefined, [WORKSPACE_HREF_ATTR]: `${tenantId.startsWith('p_') ? '/~' : `/t/${encodeURIComponent(tenantId)}`}/workspace/${encodeURIComponent(folderId)}` }}>
            {ws.tabs.map(tab => (
              <div
                key={tab.id}
                className="flex-1 flex flex-col"
                style={{ display: tab.id === ws.activeTabId ? undefined : 'none', minHeight: 0 }}
              >
                <ErrorBoundary
                  fallback={
                    <div className="flex-1 flex items-center justify-center themed-text-muted">
                      <div className="text-center">
                        <AlertCircle size={48} className="mx-auto mb-4 text-red-500 opacity-50" />
                        <p className="themed-text mb-1">Editor crashed</p>
                        <p className="text-xs opacity-60 mb-3">Something went wrong loading this file</p>
                        <button
                          onClick={() => ws.closeTab(tab.id)}
                          className="text-xs themed-btn-secondary px-3 py-1 rounded"
                        >
                          Close tab
                        </button>
                      </div>
                    </div>
                  }
                >
                  {/* #897 — editors here keep unsaved drafts across a phone/desktop
                      swap under this workspace's folder (the phone workspace uses
                      the same id), never another workspace's. */}
                  <DraftScopeProvider scope={`ws:${folderId}`}>
                  <TabContent
                    ref={getTabEditorRef(tab.id)}
                    tab={tab}
                    tenantId={tenantId}
                    isActive={tab.id === ws.activeTabId}
                    versionPreviewOpen={versionPreviewOpen}
                    revealLine={revealByFile[tab.fileId]}
                    // Whether this WORKSPACE is the deck on screen — a
                    // different question from which tab is active inside it.
                    // Workspaces stay mounted behind one another with
                    // display:none, so without this every one of them claims
                    // Ctrl+Z and the last mounted wins.
                    deckActive={isActive}
                    onDirtyChange={getTabDirtyHandler(tab.id)}
                    onConflictChange={getTabConflictHandler(tab.id)}
                    onCoverChange={getTabCoverHandler(tab.id)}
                    saveRef={getTabSaveRef(tab.id)}
                    discardRef={getTabDiscardRef(tab.id)}
                    viewMode={editorViewModesRef.current[tab.fileId]}
                    onViewModeChange={(mode) => setEditorViewMode(tab.fileId, mode)}
                    scrollRef={editorScrollRef.current}
                    viewSync={viewSyncRef.current}
                    scrollSyncTrigger={scrollSyncTrigger}
                    onExportToDrive={(blob, name, parentId, title) => setExportTarget({ blob, name, parentId: parentId ?? null, title })}
                    onOpenFile={handleOpenFile}
                    onOpenFileTarget={handleOpenFileTarget}
                    onOpenPdappTarget={handleOpenPdappTarget}
                    onCloseTab={() => ws.closeTab(tab.id)}
                  />
                  </DraftScopeProvider>
                </ErrorBoundary>
              </div>
            ))}
            {ws.tabs.length === 0 && (
              <div className="flex-1 flex items-center justify-center themed-text-muted">
                <div className="text-center">
                  <Code size={48} className="mx-auto mb-4 opacity-30" />
                  <p>Open a file from the Explorer or Drive</p>
                  <p className="text-xs mt-2 opacity-60">
                    {/* Names the two sidebar sections. "workspace files" was a
                        term the UI no longer uses anywhere. */}
                    Browse Linked from Drives or In this {ws.entityKind === 'miniapp' ? 'PDApp' : 'Project'} in the sidebar
                  </p>
                </div>
              </div>
            )}

            {/* Version preview — overlays editor only (not the history panel) */}
            {previewVersionId && versionPanelFileId && (
              <div className="absolute inset-0 z-10 themed-bg-primary flex flex-col overflow-hidden">
                <VersionPreview
                  key={identityEpoch}
                  fileId={versionPanelFileId}
                  fileName={ws.activeTab?.title ?? ''}
                  versionId={previewVersionId}
                  onBack={closePreview}
                  viewMode={getEditorViewMode(versionPanelFileId)}
                  onViewModeChange={(mode) => setEditorViewMode(versionPanelFileId, mode)}
                  scrollRef={editorScrollRef.current}
                  viewSync={viewSyncRef.current}
                  isLatest={previewIsLatest}
                  isDirty={ws.activeTab?.isDirty && !activeCover}   // no Discard offered under the editor's own cover
                  onDiscard={() => {
                    // The preview's own Discard (deliberate — it closes the preview) passes the preview half of the
                    // backstop, but never the editor's OWN cover (the conflict panel hides the live document).
                    if (ws.activeTabId && !tabHasOwnCover(ws.activeTabId)) tabDiscardRefs.current.get(ws.activeTabId)?.current?.()
                  }}
                />
              </div>
            )}

            {/* App-version preview (#612) — the app's main HTML at the selected
                app-version, overlaying the running app. Read-only, per-file
                restore hidden (the whole-app restore is on the App history
                panel's bar). Mutually exclusive with the per-file preview
                above — only one history panel is open at a time. */}
            {appPreview && !previewVersionId && (
              <div className="absolute inset-0 z-10 themed-bg-primary flex flex-col overflow-hidden">
                <VersionPreview
                  key={identityEpoch}
                  fileId={appPreview.fileId}
                  versionId={appPreview.versionId}
                  onBack={() => setAppPreview(null)}
                  isLatest={appPreview.isLatest}
                  hideActions
                  versionLabelOverride={`v${appPreview.versionNumber}`}
                />
              </div>
            )}
          </div>

          {/* Version history side panel */}
          {versionPanelFileId && (
            <div className="w-72 shrink-0 border-l themed-border overflow-hidden">
              <VersionPanel
                fileId={versionPanelFileId}
                fileName={ws.activeTab?.title ?? ''}
                tenantId={tenantId}
                onManageRetention={() => {
                  const prefix = tenantId.startsWith('p_') ? '/~' : `/t/${tenantId}`
                  navigate(`${prefix}/settings/version-retention?file=${versionPanelFileId}`)
                }}
                onClose={() => { setVersionPanelFileId(null); closePreview() }}
                onPreview={(versionId, isLatest) => {
                  const newId = versionId || null
                  // viewSyncRef[fileId] is kept up-to-date by the main editor
                  // (periodic save every 3s). Version preview reads it on mount.
                  if (!newId && previewVersionId) setScrollSyncTrigger(v => v + 1)
                  setPreviewVersionId(newId)
                  setPreviewIsLatest(isLatest)
                }}
                selectedVersion={previewVersionId}
                isDirty={ws.activeTab?.isDirty && !activeCover}   // no Discard offered under the editor's own cover
                onDiscard={() => {
                  // Never under the editor's own cover (the conflict panel): it would throw away the hidden document.
                  if (ws.activeTabId && !tabHasOwnCover(ws.activeTabId)) tabDiscardRefs.current.get(ws.activeTabId)?.current?.()
                }}
              />
            </div>
          )}

          {/* App-level version history (#612) — same side-panel slot, mutually
              exclusive with the per-file VersionPanel above. Scoped to the
              ACTIVE tab's pdapp (`appTabPdappId`), so a nested child `.pdapp`
              shows its OWN app history, not the parent's. */}
          {showAppVersionPanel && appTab && (
            <div className="w-72 shrink-0 border-l themed-border overflow-hidden">
              <AppVersionPanel
                pdappId={appTab.id}
                pdappName={appTab.name}
                tenantId={tenantId}
                onManageRetention={() => {
                  const prefix = tenantId.startsWith('p_') ? '/~' : `/t/${tenantId}`
                  navigate(`${prefix}/settings/version-retention?pdapp=${appTab.id}`)
                }}
                onPreview={(fileId, versionId, isLatest, versionNumber) => {
                  setAppPreview(versionId ? { fileId, versionId, isLatest, versionNumber } : null)
                }}
                onClose={() => { setShowAppVersionPanel(false); closePreview() }}
              />
            </div>
          )}
        </div>

        {/* Drop overlay — shown ONLY for external OS-file drags (uploads).
            Internal file/tab drags open silently, browser-tab style (#229). */}
        {editorDropActive && (
          <div className="absolute inset-0 z-40 bg-green-600/10 border-2 border-dashed border-green-500 rounded flex items-center justify-center pointer-events-none">
            <div className="flex flex-col items-center gap-2 themed-text">
              <Upload size={32} className="text-green-400" />
              <p className="text-sm font-medium">Drop to upload and open</p>
              <p className="text-xs themed-text-muted">Files will be added to this {ws.entityKind === 'miniapp' ? 'PDApp' : 'Project'}</p>
            </div>
          </div>
        )}
      </div>

      {/* Chat panel — always mounted (ChatPanel itself lazy-mounts); collapse controls its width */}
      {!isMountRootedWs && (
        <>
          {/* Always rendered — see explorer handle above. When collapsed this
              sits at the freed right edge (chat width + margin both 0). */}
          <ResizeHandle
            onPointerDown={(e) => beginResize('chat', e, chatCollapsed)}
            dragging={resizing === 'chat'}
          />
          <div ref={chatContainerRef} style={{ width: chatCollapsed ? 0 : ws.chatWidth, marginRight: chatCollapsed ? 0 : PANEL_INSET, marginLeft: chatCollapsed ? COLLAPSE_TRIM : undefined }} className="ws-panel-collapsible shrink-0 min-w-0 overflow-hidden">
            {chatMounted && <ChatPanel workspaceId={ws.workspaceId ?? undefined} workspaceFolderId={ws.folderId} entityKind={ws.entityKind} links={ws.configLoaded ? ws.links : undefined} canWrite={canWrite} activeTab={activeTabInfo} editorSelectionRef={editorSelectionRef} contextMutedRef={contextMutedRef} onMuteContext={handleMuteContext} orgEmail={orgEmail} onOpenBilling={() => navigate(`${tenantId.startsWith('p_') ? '/~' : `/t/${tenantId}`}/settings/subscription`)} onManageChatStorage={() => navigate(`${tenantId.startsWith('p_') ? '/~' : `/t/${tenantId}`}/settings/storage`)} onOpenFile={async (path, at) => {
              try {
                const result = await filesApi.resolvePath(path)
                if (!result?.id) {
                  toast.error(`File not found: ${path}`)
                  return
                }
                if (result.file.isFolder) {
                  // A `.miniapp` opens as a run tab in the editor (the app
                  // preview), not just revealed in the tree.
                  if (isMiniappFolder(result.file.name)) {
                    requestMiniappView(result.id, 'run')
                    ws.openFile(result.id, { name: result.file.name })
                  } else {
                    explorerRef.current?.revealFile(result.id)
                  }
                } else {
                  ws.openFile(result.id)
                  requestReveal(result.id, at?.line)
                }
              } catch {
                toast.error(`Could not open: ${path}`)
              }
            }} />}
          </div>
        </>
      )}
      </div>

      {/* File picker dialog */}
      {showFilePicker && (
        <FilePickerDialog
          folderId={ws.folderId}
          entityKind={ws.entityKind}
          links={ws.links}
          onSelect={(fileId) => {
            setShowFilePicker(false)
            ws.openFile(fileId)
            setEditContext('editor')
          }}
          onClose={() => setShowFilePicker(false)}
        />
      )}

      {/* Relink dialog for missing linked folders */}
      {showRelinkDialog && (
        <RelinkDialog
          missingLinks={originalMissingLinksRef.current}
          onApply={(decisions) => {
            setShowRelinkDialog(false)
            ws.submitRelinks(decisions)
          }}
          onClose={() => setShowRelinkDialog(false)}
          onPickFolder={(hint) => new Promise<{ id: string; name: string } | null>(resolve => {
            relinkResolveRef.current = resolve as any
            setRelinkPickerHint(hint)
            setShowRelinkPicker(true)
          })}
        />
      )}

      {/* Folder picker for relinking */}
      {showRelinkPicker && (
        <FilePickerDialog
          folderId={null}
          links={[]}
          foldersOnly
          subtitle={relinkPickerHint ? `Original: ${relinkPickerHint}` : undefined}
          onSelect={async (folderId) => {
            setShowRelinkPicker(false)
            try {
              const info = await fetchAndCacheFileInfo(queryClient, folderId)
              relinkResolveRef.current?.({ id: folderId, name: info.name })
            } catch {
              relinkResolveRef.current?.({ id: folderId, name: folderId })
            }
            relinkResolveRef.current = null
          }}
          onClose={() => {
            setShowRelinkPicker(false)
            relinkResolveRef.current?.(null)
            relinkResolveRef.current = null
          }}
        />
      )}

      {/* Save As dialog */}
      {saveAsTarget && (
        <SaveAsDialog
          tenantId={tenantId}
          fileId={saveAsTarget.fileId}
          defaultName={saveAsTarget.name}
          defaultFolderId={saveAsTarget.parentId}
          workspaceFolderId={ws.folderId}
          workspaceName={ws.workspaceName ?? undefined}
          entityKind={ws.entityKind}
          onSaved={(newFileId) => {
            setSaveAsTarget(null)
            // Close destination file's tab if already open (avoid duplicates)
            const existingTab = ws.tabs.find(t => t.fileId === newFileId)
            if (existingTab) ws.closeTab(existingTab.id)
            ws.replaceTab(saveAsTarget.fileId, newFileId)
            setEditContext('editor')
          }}
          saveBlockedReason={() => {
            const tab = ws.tabs.find(t => t.fileId === saveAsTarget.fileId)
            if (!tab || !isTabCovered(tab.id, tabCoversRef.current, versionPreviewOpenRef.current)) return null
            return tabCoversRef.current[tab.id]?.message ?? tCollab(PREVIEW_SAVE_BLOCKED_KEY)
          }}
          onClose={() => setSaveAsTarget(null)}
        />
      )}

      {/* Export to drive dialog (PDF export from editors etc.) */}
      {exportTarget && (
        <SaveAsDialog
          tenantId={tenantId}
          defaultName={exportTarget.name}
          defaultFolderId={exportTarget.parentId}
          title={exportTarget.title || 'Save to Drive'}
          workspaceFolderId={ws.folderId}
          workspaceName={ws.workspaceName ?? undefined}
          entityKind={ws.entityKind}
          customSave={async (destParentId, destName, keepBoth) => {
            // Replace onto an existing name = a new version of that file (the upload writes into it).
            const strategy = keepBoth ? 'keepBoth' as const : 'replace' as const
            const res = await filesApi.uploadFile(destParentId, destName, exportTarget.blob, { conflictStrategy: strategy })
            // #1090 — a large app database is replaced in the background (202).
            if (res.queued) toast.success('Saved — the database is being replaced in the background; you will be notified when it is done')
            return res
          }}
          onSaved={(newFileId) => {
            setExportTarget(null)
            ws.openFile(newFileId)
          }}
          onClose={() => setExportTarget(null)}
        />
      )}

      {/* Add Sheet dialog (CSV workbook groups — reuses SaveAsDialog) */}
      {addSheetTarget && (
        <SaveAsDialog
          tenantId={tenantId}
          defaultName={addSheetTarget.defaultName}
          defaultFolderId={addSheetTarget.parentId}
          title="Add Sheet"
          description="Enter a file name to create a new CSV, or pick an existing file to link it as a sheet."
          locationLabel={(exists) => exists ? 'Link from' : 'Create at'}
          confirmLabel={(exists) => exists ? 'Link' : 'Create'}
          skipConflictCheck
          workspaceFolderId={ws.folderId}
          workspaceName={ws.workspaceName ?? undefined}
          entityKind={ws.entityKind}
          customSave={async (destParentId, destName) => {
            lastAddSheetContextRef.current = { parentId: destParentId, sourceFileId: addSheetTarget?.sourceFileId }
            window.dispatchEvent(new CustomEvent('papan:wb-add-sheet', {
              detail: { type: 'create', fileName: destName, parentId: destParentId, sourceFileId: addSheetTarget?.sourceFileId },
            }))
            // Return a dummy result — the server handles the actual file creation/linking
            return { fileId: 'pending', name: destName }
          }}
          onSaved={() => setAddSheetTarget(null)}
          onClose={() => setAddSheetTarget(null)}
        />
      )}

      {/* Relink Sheet dialog (swap backing CSV — reuses SaveAsDialog) */}
      {relinkTarget && (
        <SaveAsDialog
          tenantId={tenantId}
          defaultName={relinkTarget.sheetName + '.csv'}
          defaultFolderId={relinkTarget.parentId}
          title={`Relink "${relinkTarget.sheetName}"`}
          description="Pick an existing CSV or create a new one. The sheet name will be kept."
          locationLabel={(exists) => exists ? 'Link from' : 'Create at'}
          confirmLabel={(exists) => exists ? 'Relink' : 'Create & Relink'}
          skipConflictCheck
          workspaceFolderId={ws.folderId}
          workspaceName={ws.workspaceName ?? undefined}
          entityKind={ws.entityKind}
          customSave={async (destParentId, destName) => {
            window.dispatchEvent(new CustomEvent('papan:wb-add-sheet', {
              detail: {
                type: 'relink',
                fileName: destName,
                parentId: destParentId,
                relinkSheetId: relinkTarget.sheetId,
                sourceFileId: relinkTarget.sourceFileId,
              },
            }))
            return { fileId: 'pending', name: destName }
          }}
          onSaved={() => setRelinkTarget(null)}
          onClose={() => setRelinkTarget(null)}
        />
      )}

      {/* Unsaved changes dialog */}
      {unsavedDialog && (
        <UnsavedChangesDialog
          fileNames={unsavedDialog.dirtyNames}
          onAction={handleUnsavedAction}
        />
      )}

      {/* Workspace picker dialog */}
      {showWorkspacePicker && (
        <WorkspacePickerDialog
          currentFolderId={folderId}
          onSelect={(wsFolderId) => {
            setShowWorkspacePicker(false)
            navigate(`/t/${tenantId}/workspace/${wsFolderId}`)
          }}
          onClose={() => setShowWorkspacePicker(false)}
        />
      )}
    </div>
    </CommandRegistryProvider>
  )
}

// ---------------------------------------------------------------------------
/**
 * #929/#930 — whether a tab's per-tab save / Save As / discard is refused: its editor's own cover (the conflict
 * "Saved version" panel), or a version preview being open — which, like Save All, refuses them on EVERY tab (the
 * rule the tab menu has had since the preview guard). The backstop behind every per-tab write in the workspace.
 */
export function isTabCovered(
  tabId: string,
  covers: Readonly<Record<string, unknown>>,
  versionPreviewOpen: boolean,
): boolean {
  return tabId in covers || versionPreviewOpen
}

// WorkspaceCommandRegistration — registers workspace-level commands + focus scopes
// Must be a child of CommandRegistryProvider to use hooks.
// ---------------------------------------------------------------------------

/** @internal exported for tests — see workspace-save-commands.test.tsx */
export function WorkspaceCommandRegistration({ explorerContainerRef, editorContainerRef: _editorContainerRef, ws, handleSaveTab, handleSaveAll, handleDiscardTab, editorCovered, saveAllAvailable }: {
  explorerContainerRef: React.RefObject<HTMLDivElement | null>
  editorContainerRef: React.RefObject<HTMLDivElement | null>
  ws: ReturnType<typeof useWorkspace>
  handleSaveTab: (tabId: string) => void
  handleSaveAll: () => void
  handleDiscardTab: (tabId: string) => void
  /** Something covers the active editor (a version preview, or its own cover) — see WorkspaceView. */
  editorCovered: boolean
  /** Save All's one rule — see WorkspaceView's `saveAllAvailable`. */
  saveAllAvailable: boolean
}) {
  // Focus scope tracking — replaces the old mousedown editContext detection
  useFocusScope('explorer', explorerContainerRef)
  // …and the same click hands Ctrl+Z back to the drive's own history, so a
  // delete made here is undoable from here. See UndoProvider.
  useUndoRegion('chrome', explorerContainerRef)
  // Don't set 'editor' scope on the container — let each editor set its own scope.
  // This prevents clicks on tab bar/breadcrumb/mode toggle from overriding
  // specific editor scopes like 'editor:db:grid'.

  // Workspace-level commands (always available regardless of focus)
  const workspaceCommands = useMemo((): Command[] => {
    const activeTab = ws.tabs.find(t => t.id === ws.activeTabId)
    // Gated on the preview even though NOTHING READS IT TODAY, and that is
    // the point. `getMenuCommands` is only ever asked for 'edit', the File
    // menu's Save / Save All / Discard are hard-wired buttons driven by the
    // `canSave` / `canSaveAll` / `canDiscard` props (where the guard that
    // currently works lives), and the registry's keyboard path (#900) does not
    // dispatch these: the 'workspace' scope has no keyboard region.
    //
    // Gated anyway: should that scope ever get a region, the registry's matcher
    // DOES lowercase the key, so Cmd+Shift+S would fire `file.saveAll` through
    // a route the props never touch. Leaving these ungated would reopen the
    // invisible save at exactly the moment nobody is looking at this file.
    const covered = editorCovered
    return [
      {
        id: 'file.save', label: 'Save',
        shortcut: { key: 's', mod: true },
        execute: () => { if (ws.activeTabId) handleSaveTab(ws.activeTabId) },
        enabled: !covered && !!activeTab?.isDirty,
        menuGroup: 'file', menuOrder: 50,
      },
      {
        id: 'file.saveAll', label: 'Save All',
        shortcut: { key: 's', mod: true, shift: true },
        execute: handleSaveAll,
        enabled: saveAllAvailable,
        menuGroup: 'file', menuOrder: 51,
      },
      {
        id: 'file.discard', label: 'Discard Changes',
        execute: () => { if (ws.activeTabId) handleDiscardTab(ws.activeTabId) },
        enabled: !covered && !!activeTab?.isDirty,
        menuGroup: 'file', menuOrder: 55,
        danger: true,
      },
    ]
  }, [ws.activeTabId, ws.tabs, handleSaveTab, handleSaveAll, handleDiscardTab, editorCovered, saveAllAvailable])

  useCommandScope('workspace', undefined, workspaceCommands)

  return null
}

/** Walk up the folder tree (max 10 levels) to check if folderId or any ancestor is in coveredIds */
async function isAncestorCovered(queryClient: QueryClient, folderId: string, coveredIds: Set<string>): Promise<boolean> {
  let current: string | null = folderId
  for (let i = 0; i < 10 && current; i++) {
    if (coveredIds.has(current)) return true
    try {
      const info = await fetchAndCacheFileInfo(queryClient, current)
      current = info.parentId || null
    } catch {
      break
    }
  }
  return false
}
